import { z } from 'zod';
import { QUESTIONNAIRE_ALLOWED_FILE_EXTENSIONS, QUESTIONNAIRE_ALLOWED_FILE_MIME_TYPES, QUESTIONNAIRE_ALLOWED_IMAGE_EXTENSIONS, QUESTIONNAIRE_ALLOWED_IMAGE_MIME_TYPES, QUESTIONNAIRE_AGGREGATABLE_QUESTION_TYPES, QUESTIONNAIRE_ANSWER_APPLICABILITIES, QUESTIONNAIRE_APPLICATION_SCOPES, QUESTIONNAIRE_ATTACHMENT_PROCESSING_STATUSES, QUESTIONNAIRE_COMPLETION_ACTION_STATUSES, QUESTIONNAIRE_COMPLETION_ACTION_TYPES, QUESTIONNAIRE_CUSTOMER_IDLE_LOCK_MINUTES_DEFAULT, QUESTIONNAIRE_CUSTOMER_SESSION_TTL_MINUTES_DEFAULT, QUESTIONNAIRE_DRAFT_CLEANUP_DAYS_DEFAULT, QUESTIONNAIRE_HIDDEN_ANSWER_POLICIES, QUESTIONNAIRE_IMAGE_SOURCES, QUESTIONNAIRE_LOGIC_ACTIONS, QUESTIONNAIRE_LOGIC_COMBINATORS, QUESTIONNAIRE_LOGIC_OPERATORS, QUESTIONNAIRE_MAX_CONDITIONS_PER_RULE, QUESTIONNAIRE_MAX_FILE_SIZE_BYTES, QUESTIONNAIRE_MAX_FILES_PER_QUESTION, QUESTIONNAIRE_MAX_LOGIC_RULES, QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS, QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION, QUESTIONNAIRE_MAX_PATTERN_LENGTH, QUESTIONNAIRE_MAX_PATTERN_VARIABILITY, QUESTIONNAIRE_MAX_QUESTIONS, QUESTIONNAIRE_MAX_QUESTIONS_PER_SECTION, QUESTIONNAIRE_MAX_REPEATABLE_ROWS, QUESTIONNAIRE_MAX_SECTIONS, QUESTIONNAIRE_MAX_SHORT_TEXT_LENGTH, QUESTIONNAIRE_MAX_TEXT_LENGTH, QUESTIONNAIRE_PAGE_SIZE_DEFAULT, QUESTIONNAIRE_PAGE_SIZE_MAX, QUESTIONNAIRE_PARTNER_CONVERSION_STATUSES, QUESTIONNAIRE_PARTNER_CONVERSION_TARGET_ROLES, QUESTIONNAIRE_POST_PROCESSING_STATUSES, QUESTIONNAIRE_PRODUCT_INTEREST_LEVELS, QUESTIONNAIRE_QUESTION_TYPES, QUESTIONNAIRE_RETENTION_DAYS_DEFAULT, QUESTIONNAIRE_RUN_STATUSES, QUESTIONNAIRE_SENSITIVE_CLASSIFICATIONS, QUESTIONNAIRE_SYNC_EVENT_STATUSES, QUESTIONNAIRE_SYNC_EVENT_TYPES, QUESTIONNAIRE_SYNC_STATUSES, QUESTIONNAIRE_TEMPLATE_STATUSES, } from '../constants/questionnaires.js';
import { currencyCodeSchema, idSchema, isoDateTimeSchema, moneySchema, nonNegativeQuantitySchema, } from './common.js';
import { paginationMetaSchema } from './pagination.js';
const questionnaireKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9_]*$/, 'Keys must start with a letter and use lowercase characters.');
const questionnaireLocaleSchema = z
    .string()
    .trim()
    .min(2)
    .max(35)
    .regex(/^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/);
const questionnaireLabelSchema = z.string().trim().min(1).max(500);
const questionnaireOptionalTextSchema = z.string().trim().max(QUESTIONNAIRE_MAX_TEXT_LENGTH);
function isValidQuestionnaireDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
    if (match === null)
        return false;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day);
}
export const questionnaireIsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u)
    .refine(isValidQuestionnaireDate, 'The date must be a valid calendar date.');
const questionnaireTimeAnswerSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/u);
export const questionnaireTemplateStatusSchema = z.enum(QUESTIONNAIRE_TEMPLATE_STATUSES);
export const questionnaireApplicationScopeSchema = z.enum(QUESTIONNAIRE_APPLICATION_SCOPES);
export const questionnaireQuestionTypeSchema = z.enum(QUESTIONNAIRE_QUESTION_TYPES);
export const questionnaireRunStatusSchema = z.enum(QUESTIONNAIRE_RUN_STATUSES);
export const questionnaireSyncStatusSchema = z.enum(QUESTIONNAIRE_SYNC_STATUSES);
export const questionnairePostProcessingStatusSchema = z.enum(QUESTIONNAIRE_POST_PROCESSING_STATUSES);
export const questionnaireAnswerApplicabilitySchema = z.enum(QUESTIONNAIRE_ANSWER_APPLICABILITIES);
export const questionnaireProductInterestLevelSchema = z.enum(QUESTIONNAIRE_PRODUCT_INTEREST_LEVELS);
export const questionnaireAttachmentProcessingStatusSchema = z.enum(QUESTIONNAIRE_ATTACHMENT_PROCESSING_STATUSES);
export const questionnaireCompletionActionTypeSchema = z.enum(QUESTIONNAIRE_COMPLETION_ACTION_TYPES);
export const questionnaireCompletionActionStatusSchema = z.enum(QUESTIONNAIRE_COMPLETION_ACTION_STATUSES);
export const questionnairePartnerConversionTargetRoleSchema = z.enum(QUESTIONNAIRE_PARTNER_CONVERSION_TARGET_ROLES);
export const questionnairePartnerConversionStatusSchema = z.enum(QUESTIONNAIRE_PARTNER_CONVERSION_STATUSES);
export const questionnaireSyncEventTypeSchema = z.enum(QUESTIONNAIRE_SYNC_EVENT_TYPES);
export const questionnaireSyncEventStatusSchema = z.enum(QUESTIONNAIRE_SYNC_EVENT_STATUSES);
export const questionnaireSensitiveClassificationSchema = z.enum(QUESTIONNAIRE_SENSITIVE_CLASSIFICATIONS);
/**
 * Native JavaScript regular expressions have no execution timeout. Builder patterns
 * therefore use a deliberately small linear subset: no groups, alternation, wildcard,
 * unbounded quantifiers, lookarounds, or backreferences. Optional and bounded
 * quantifiers are accepted only below a strict aggregate variability budget.
 */
export function isSafeQuestionnairePattern(pattern) {
    if (pattern.length === 0 || pattern.length > QUESTIONNAIRE_MAX_PATTERN_LENGTH)
        return false;
    let canQuantify = false;
    let wasQuantified = false;
    let variability = 1;
    for (let index = 0; index < pattern.length; index += 1) {
        const character = pattern[index];
        if (character === undefined)
            return false;
        if (character === '\\') {
            const escaped = pattern[index + 1];
            if (escaped === undefined || /^[1-9]$/u.test(escaped))
                return false;
            index += 1;
            canQuantify = true;
            wasQuantified = false;
            continue;
        }
        if (character === '[') {
            let closed = false;
            let classHasContent = false;
            for (index += 1; index < pattern.length; index += 1) {
                const classCharacter = pattern[index];
                if (classCharacter === undefined)
                    return false;
                if (classCharacter === '\\') {
                    if (pattern[index + 1] === undefined)
                        return false;
                    index += 1;
                    classHasContent = true;
                    continue;
                }
                if (classCharacter === ']' && classHasContent) {
                    closed = true;
                    break;
                }
                if (classCharacter === '[')
                    return false;
                if (!(classCharacter === '^' && !classHasContent))
                    classHasContent = true;
            }
            if (!closed)
                return false;
            canQuantify = true;
            wasQuantified = false;
            continue;
        }
        if (character === '?' || character === '{') {
            if (!canQuantify || wasQuantified)
                return false;
            let alternatives = 2;
            if (character === '{') {
                const closingIndex = pattern.indexOf('}', index + 1);
                if (closingIndex === -1)
                    return false;
                const bounds = /^(\d{1,3})(?:,(\d{1,3}))?$/u.exec(pattern.slice(index + 1, closingIndex));
                if (bounds === null)
                    return false;
                const lower = Number(bounds[1]);
                const upper = bounds[2] === undefined ? lower : Number(bounds[2]);
                if (lower > upper || upper > 100)
                    return false;
                alternatives = upper - lower + 1;
                index = closingIndex;
            }
            variability *= alternatives;
            if (variability > QUESTIONNAIRE_MAX_PATTERN_VARIABILITY)
                return false;
            wasQuantified = true;
            canQuantify = false;
            continue;
        }
        if (character === '^' || character === '$') {
            if ((character === '^' && index !== 0) || (character === '$' && index !== pattern.length - 1))
                return false;
            canQuantify = false;
            wasQuantified = false;
            continue;
        }
        if (/[().*+|\]{}]/u.test(character))
            return false;
        canQuantify = true;
        wasQuantified = false;
    }
    try {
        new RegExp(pattern, 'u');
        return true;
    }
    catch {
        return false;
    }
}
export const questionnaireSafePatternSchema = z
    .string()
    .max(QUESTIONNAIRE_MAX_PATTERN_LENGTH)
    .refine(isSafeQuestionnairePattern, 'The pattern is outside the supported safe subset.');
export const questionnaireTranslationSchema = z
    .object({
    title: z.string().trim().max(500).optional(),
    description: questionnaireOptionalTextSchema.optional(),
    label: z.string().trim().max(500).optional(),
    helpText: questionnaireOptionalTextSchema.optional(),
    placeholder: z.string().trim().max(500).optional(),
    greetingText: questionnaireOptionalTextSchema.optional(),
    completionText: questionnaireOptionalTextSchema.optional(),
    privacyText: questionnaireOptionalTextSchema.optional(),
})
    .strict();
export const questionnaireTranslationsSchema = z.record(questionnaireLocaleSchema, questionnaireTranslationSchema);
export const questionnaireQuestionOptionSchema = z
    .object({
    id: idSchema.optional(),
    key: questionnaireKeySchema,
    value: z.string().trim().min(1).max(200),
    label: questionnaireLabelSchema,
    description: questionnaireOptionalTextSchema.optional(),
    sortOrder: z.number().int().min(0),
    translations: questionnaireTranslationsSchema.default({}),
})
    .strict();
const questionnaireTextConfigSchema = z
    .object({
    minLength: z.number().int().min(0).max(QUESTIONNAIRE_MAX_TEXT_LENGTH).optional(),
    maxLength: z.number().int().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH).optional(),
    pattern: questionnaireSafePatternSchema.optional(),
    autocomplete: z.string().trim().max(100).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.minLength !== undefined &&
        value.maxLength !== undefined &&
        value.minLength > value.maxLength) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxLength'],
            message: 'maxLength must be greater than or equal to minLength.',
        });
    }
});
const questionnaireNumberConfigSchema = z
    .object({
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    decimalPlaces: z.number().int().min(0).max(6).default(2),
    step: z.number().positive().finite().optional(),
    currency: currencyCodeSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.min !== undefined && value.max !== undefined && value.min > value.max) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['max'],
            message: 'max must be greater than or equal to min.',
        });
    }
});
function questionnaireBoundedDateConfigSchema(valueSchema) {
    return z
        .object({ min: valueSchema.optional(), max: valueSchema.optional() })
        .strict()
        .refine((value) => value.min === undefined || value.max === undefined || value.min <= value.max, { path: ['max'], message: 'max must be greater than or equal to min.' });
}
const questionnaireDateConfigSchema = questionnaireBoundedDateConfigSchema(questionnaireIsoDateSchema);
const questionnaireTimeConfigSchema = questionnaireBoundedDateConfigSchema(questionnaireTimeAnswerSchema);
const questionnaireDateTimeConfigSchema = questionnaireBoundedDateConfigSchema(isoDateTimeSchema);
const questionnaireChoiceConfigSchema = z
    .object({
    options: z
        .array(questionnaireQuestionOptionSchema)
        .min(1)
        .max(QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION),
    randomizeOptions: z.boolean().default(false),
    minSelections: z.number().int().min(0).optional(),
    maxSelections: z.number().int().min(1).optional(),
    searchable: z.boolean().default(false),
})
    .strict()
    .superRefine((value, context) => {
    const keys = new Set();
    const optionValues = new Set();
    for (const option of value.options) {
        if (keys.has(option.key) ||
            optionValues.has(option.value) ||
            value.options.some((candidate) => candidate !== option && candidate.sortOrder === option.sortOrder)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['options'],
                message: 'Option keys, values and sort orders must be unique.',
            });
        }
        keys.add(option.key);
        optionValues.add(option.value);
    }
    if (value.minSelections !== undefined &&
        value.maxSelections !== undefined &&
        value.minSelections > value.maxSelections) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxSelections'],
            message: 'maxSelections must be greater than or equal to minSelections.',
        });
    }
    if (value.maxSelections !== undefined && value.maxSelections > value.options.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxSelections'],
            message: 'maxSelections cannot exceed the number of options.',
        });
    }
});
const questionnaireRatingConfigSchema = z
    .object({
    min: z.number().int().min(0).max(100),
    max: z.number().int().min(1).max(100),
    step: z.number().positive().max(100).default(1),
    minLabel: z.string().trim().max(200).optional(),
    maxLabel: z.string().trim().max(200).optional(),
})
    .strict()
    .refine((value) => value.max > value.min, {
    path: ['max'],
    message: 'max must be greater than min.',
});
const questionnaireAddressConfigSchema = z
    .object({
    requireStreet: z.boolean().default(false),
    requirePostalCode: z.boolean().default(false),
    requireCity: z.boolean().default(false),
    requireCountry: z.boolean().default(false),
})
    .strict();
const questionnaireFileConfigSchema = z
    .object({
    allowedExtensions: z
        .array(z.enum(QUESTIONNAIRE_ALLOWED_FILE_EXTENSIONS))
        .min(1)
        .default([...QUESTIONNAIRE_ALLOWED_FILE_EXTENSIONS]),
    allowedMimeTypes: z
        .array(z.enum(QUESTIONNAIRE_ALLOWED_FILE_MIME_TYPES))
        .min(1)
        .default([...QUESTIONNAIRE_ALLOWED_FILE_MIME_TYPES]),
    maxFiles: z.number().int().min(1).max(QUESTIONNAIRE_MAX_FILES_PER_QUESTION).default(1),
    maxFileSizeBytes: z
        .number()
        .int()
        .min(1)
        .max(QUESTIONNAIRE_MAX_FILE_SIZE_BYTES)
        .default(QUESTIONNAIRE_MAX_FILE_SIZE_BYTES),
    requireDescription: z.boolean().default(false),
})
    .strict();
const questionnaireImageConfigSchema = questionnaireFileConfigSchema.extend({
    allowedExtensions: z
        .array(z.enum(QUESTIONNAIRE_ALLOWED_IMAGE_EXTENSIONS))
        .min(1)
        .default([...QUESTIONNAIRE_ALLOWED_IMAGE_EXTENSIONS]),
    allowedMimeTypes: z
        .array(z.enum(QUESTIONNAIRE_ALLOWED_IMAGE_MIME_TYPES))
        .min(1)
        .default([...QUESTIONNAIRE_ALLOWED_IMAGE_MIME_TYPES]),
    source: z.enum(QUESTIONNAIRE_IMAGE_SOURCES).default('BOTH'),
    allowMultiple: z.boolean().default(false),
    allowCrop: z.boolean().default(true),
});
const questionnaireConsentConfigSchema = z
    .object({
    text: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    purpose: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    documentVersion: z.string().trim().min(1).max(100),
    withdrawalNotice: questionnaireOptionalTextSchema.optional(),
    requireName: z.boolean().default(false),
    requireSignature: z.boolean().default(false),
})
    .strict();
export const questionnaireProductFilterSchema = z
    .object({
    productIds: z.array(idSchema).max(500).default([]),
    categoryIds: z.array(idSchema).max(100).default([]),
    productGroups: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
    tags: z.array(z.string().trim().min(1).max(100)).max(100).default([]),
    productTypes: z.array(z.enum(['STOCK', 'NON_STOCK', 'SERVICE', 'SET', 'DEPOSIT'])).default([]),
    activeOnly: z.boolean().default(true),
    customerSpecificAssortment: z.boolean().default(false),
    availableOnly: z.boolean().default(false),
    rootCategoryId: idSchema.optional(),
    includeSubcategories: z.boolean().default(true),
})
    .strict()
    .superRefine((filters, context) => {
    const collections = [
        filters.productIds,
        filters.categoryIds,
        filters.productGroups,
        filters.tags,
        filters.productTypes,
    ];
    if (collections.some((entries) => new Set(entries).size !== entries.length)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Product filter values must be unique.',
        });
    }
});
export const questionnaireProductDisplaySchema = z
    .object({
    showImage: z.boolean().default(true),
    showName: z.boolean().default(true),
    showArticleNumber: z.boolean().default(true),
    showDescription: z.boolean().default(false),
    showPackaging: z.boolean().default(true),
    showPrice: z.boolean().default(false),
    showStock: z.boolean().default(false),
    showCategories: z.boolean().default(false),
    showTags: z.boolean().default(false),
    allowQuantity: z.boolean().default(false),
    allowInterest: z.boolean().default(true),
    allowCurrentUse: z.boolean().default(false),
    allowPlannedPurchaseDate: z.boolean().default(false),
    allowCompetitorProduct: z.boolean().default(false),
    allowRemark: z.boolean().default(false),
    allowComparison: z.boolean().default(false),
    allowProductPhoto: z.boolean().default(false),
    allowWatchlist: z.boolean().default(false),
})
    .strict();
const questionnaireProductConfigSchema = z
    .object({
    filters: questionnaireProductFilterSchema,
    display: questionnaireProductDisplaySchema,
    minSelections: z.number().int().min(0).optional(),
    maxSelections: z.number().int().min(1).max(500).optional(),
})
    .strict()
    .refine((value) => value.minSelections === undefined ||
    value.maxSelections === undefined ||
    value.minSelections <= value.maxSelections, { path: ['maxSelections'], message: 'maxSelections must not be below minSelections.' });
const questionnaireRepeatableGroupConfigSchema = z
    .object({
    childQuestionKeys: z.array(questionnaireKeySchema).min(1).max(50),
    minRows: z.number().int().min(0).max(QUESTIONNAIRE_MAX_REPEATABLE_ROWS).default(0),
    maxRows: z
        .number()
        .int()
        .min(1)
        .max(QUESTIONNAIRE_MAX_REPEATABLE_ROWS)
        .default(QUESTIONNAIRE_MAX_REPEATABLE_ROWS),
    addButtonLabel: z.string().trim().min(1).max(200),
})
    .strict()
    .refine((value) => value.minRows <= value.maxRows, {
    path: ['maxRows'],
    message: 'maxRows must not be below minRows.',
});
const questionnaireEmptyConfigSchema = z.object({}).strict();
const questionnaireQuestionBaseSchema = z
    .object({
    id: idSchema.optional(),
    key: questionnaireKeySchema,
    label: questionnaireLabelSchema,
    helpText: questionnaireOptionalTextSchema.optional(),
    placeholder: z.string().trim().max(500).optional(),
    required: z.boolean().default(false),
    defaultValue: z
        .union([z.string(), z.number().finite(), z.boolean(), z.array(z.string()), z.null()])
        .optional(),
    validationMessage: z.string().trim().max(500).optional(),
    visibleInCustomerMode: z.boolean().default(true),
    internal: z.boolean().default(false),
    prefillPartnerField: z.string().trim().max(200).optional(),
    erpTargetField: z.string().trim().max(200).optional(),
    analyticsKey: questionnaireKeySchema.optional(),
    searchable: z.boolean().default(false),
    sensitiveClassification: questionnaireSensitiveClassificationSchema.default('NONE'),
    sortOrder: z.number().int().min(0),
    translations: questionnaireTranslationsSchema.default({}),
})
    .strict();
export const questionnaireQuestionSchema = z.discriminatedUnion('type', [
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('SHORT_TEXT'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('LONG_TEXT'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('NUMBER'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('DECIMAL'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('CURRENCY'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PERCENT'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('DATE'),
        config: questionnaireDateConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('TIME'),
        config: questionnaireTimeConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('DATE_TIME'),
        config: questionnaireDateTimeConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('YES_NO'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('CHECKBOX'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('SINGLE_CHOICE'),
        config: questionnaireChoiceConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('MULTIPLE_CHOICE'),
        config: questionnaireChoiceConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('DROPDOWN'),
        config: questionnaireChoiceConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('COMBOBOX'),
        config: questionnaireChoiceConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('RATING_SCALE'),
        config: questionnaireRatingConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('STAR_RATING'),
        config: questionnaireRatingConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('SLIDER'),
        config: questionnaireRatingConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('EMAIL'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PHONE'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('URL'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('ADDRESS'),
        config: questionnaireAddressConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('HEADING'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('INFORMATION'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('DIVIDER'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('FILE_UPLOAD'),
        config: questionnaireFileConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PHOTO'),
        config: questionnaireImageConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('IMAGE_GALLERY'),
        config: questionnaireImageConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('SIGNATURE'),
        config: z.object({ requireName: z.boolean().default(false) }).strict(),
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('CONSENT'),
        config: questionnaireConsentConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_SINGLE'),
        config: questionnaireProductConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_MULTI'),
        config: questionnaireProductConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_CATALOG'),
        config: questionnaireProductConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_QUANTITY_LIST'),
        config: questionnaireProductConfigSchema,
    }),
    questionnaireQuestionBaseSchema.extend({
        type: z.literal('REPEATABLE_GROUP'),
        config: questionnaireRepeatableGroupConfigSchema,
    }),
]);
export const questionnaireSectionSchema = z
    .object({
    id: idSchema.optional(),
    key: questionnaireKeySchema,
    title: questionnaireLabelSchema,
    description: questionnaireOptionalTextSchema.optional(),
    translations: questionnaireTranslationsSchema.default({}),
    sortOrder: z.number().int().min(0),
    pageBreakBefore: z.boolean().default(false),
    isCompletionSection: z.boolean().default(false),
    visibleInCustomerMode: z.boolean().default(true),
    questions: z.array(questionnaireQuestionSchema).max(QUESTIONNAIRE_MAX_QUESTIONS_PER_SECTION),
})
    .strict();
export const questionnaireLogicScalarSchema = z.union([
    z.string().max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);
export const questionnaireLogicValueSchema = z.union([
    questionnaireLogicScalarSchema,
    z.array(questionnaireLogicScalarSchema).max(QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION),
]);
export const questionnaireLogicConditionSchema = z
    .object({
    sourceQuestionKey: questionnaireKeySchema,
    operator: z.enum(QUESTIONNAIRE_LOGIC_OPERATORS),
    value: questionnaireLogicValueSchema.optional(),
})
    .strict()
    .superRefine((condition, context) => {
    const valueMustBeAbsent = condition.operator === 'EMPTY' || condition.operator === 'NOT_EMPTY';
    if (valueMustBeAbsent && condition.value !== undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'EMPTY and NOT_EMPTY must not define a comparison value.',
        });
    }
    if (!valueMustBeAbsent && condition.value === undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'This operator requires a comparison value.',
        });
    }
    if (condition.operator === 'BETWEEN' &&
        (!Array.isArray(condition.value) || condition.value.length !== 2)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'BETWEEN requires exactly two boundary values.',
        });
    }
    if ((condition.operator === 'ONE_OF' || condition.operator === 'ALL_OF') &&
        (!Array.isArray(condition.value) || condition.value.length === 0)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'ONE_OF and ALL_OF require a non-empty comparison list.',
        });
    }
    if ((condition.operator === 'CONTAINS' || condition.operator === 'NOT_CONTAINS') &&
        Array.isArray(condition.value)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'CONTAINS and NOT_CONTAINS require a scalar comparison value.',
        });
    }
    if ((condition.operator === 'GREATER_THAN' ||
        condition.operator === 'LESS_THAN' ||
        condition.operator === 'BETWEEN') &&
        (Array.isArray(condition.value) ? condition.value : [condition.value]).some((boundary) => (typeof boundary !== 'number' && typeof boundary !== 'string') ||
            !Number.isFinite(Number(boundary)))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'Numeric comparison operators require finite numeric values.',
        });
    }
    if (condition.operator === 'BETWEEN' &&
        Array.isArray(condition.value) &&
        condition.value.length === 2 &&
        Number(condition.value[0]) > Number(condition.value[1])) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'BETWEEN lower boundary must not exceed its upper boundary.',
        });
    }
});
export const questionnaireLogicTargetSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('QUESTION'), key: questionnaireKeySchema }).strict(),
    z.object({ kind: z.literal('SECTION'), key: questionnaireKeySchema }).strict(),
]);
export const questionnaireLogicRuleSchema = z
    .object({
    id: idSchema.optional(),
    key: questionnaireKeySchema,
    combinator: z.enum(QUESTIONNAIRE_LOGIC_COMBINATORS),
    action: z.enum(QUESTIONNAIRE_LOGIC_ACTIONS),
    target: questionnaireLogicTargetSchema,
    conditions: z
        .array(questionnaireLogicConditionSchema)
        .min(1)
        .max(QUESTIONNAIRE_MAX_CONDITIONS_PER_RULE),
    sortOrder: z.number().int().min(0),
})
    .strict()
    .superRefine((rule, context) => {
    if (rule.action === 'SKIP_TO_SECTION' && rule.target.kind !== 'SECTION') {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target'],
            message: 'SKIP_TO_SECTION requires a section target.',
        });
    }
});
export const questionnaireCustomerModeConfigSchema = z
    .object({
    enabled: z.boolean().default(true),
    allowBackNavigation: z.boolean().default(true),
    fullscreenEnabled: z.boolean().default(false),
    idleLockMinutes: z.number().int().min(1).max(60).default(5),
})
    .strict();
export const questionnaireOfflineConfigSchema = z
    .object({
    enabled: z.boolean().default(false),
    allowSensitiveData: z.boolean().default(false),
    preloadProductLimit: z.number().int().min(0).max(QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS).default(0),
})
    .strict();
export const questionnairePresentationConfigSchema = z
    .object({
    showProgress: z.boolean().default(true),
    oneQuestionPerPage: z.boolean().default(false),
    sectionPerPage: z.boolean().default(true),
    hiddenAnswerPolicy: z.enum(QUESTIONNAIRE_HIDDEN_ANSWER_POLICIES).default('MARK_NOT_APPLICABLE'),
})
    .strict();
export const questionnaireDefinitionSchema = z
    .object({
    title: questionnaireLabelSchema,
    description: questionnaireOptionalTextSchema.optional(),
    language: questionnaireLocaleSchema.default('de'),
    translations: questionnaireTranslationsSchema.default({}),
    greetingText: questionnaireOptionalTextSchema.optional(),
    completionText: questionnaireOptionalTextSchema.optional(),
    privacyText: questionnaireOptionalTextSchema.optional(),
    estimatedDurationMinutes: z.number().int().min(1).max(1_440).optional(),
    titleImageAttachmentId: idSchema.optional(),
    useTenantLogo: z.boolean().default(true),
    applicationScopes: z
        .array(questionnaireApplicationScopeSchema)
        .min(1)
        .max(7)
        .refine((scopes) => new Set(scopes).size === scopes.length, {
        message: 'Application scopes must be unique.',
    }),
    customerMode: questionnaireCustomerModeConfigSchema,
    offline: questionnaireOfflineConfigSchema,
    presentation: questionnairePresentationConfigSchema,
    sections: z.array(questionnaireSectionSchema).min(1).max(QUESTIONNAIRE_MAX_SECTIONS),
    logicRules: z.array(questionnaireLogicRuleSchema).max(QUESTIONNAIRE_MAX_LOGIC_RULES),
})
    .strict();
const questionnaireNonAnswerTypes = new Set([
    'HEADING',
    'INFORMATION',
    'DIVIDER',
]);
const questionnaireAggregatableTypes = new Set(QUESTIONNAIRE_AGGREGATABLE_QUESTION_TYPES);
const questionnaireUnsupportedRepeatableChildTypes = new Set([
    'ADDRESS',
    'FILE_UPLOAD',
    'PHOTO',
    'IMAGE_GALLERY',
    'SIGNATURE',
    'CONSENT',
    'PRODUCT_SINGLE',
    'PRODUCT_MULTI',
    'PRODUCT_CATALOG',
    'PRODUCT_QUANTITY_LIST',
    'REPEATABLE_GROUP',
]);
export function validateQuestionnaireDefinition(input) {
    const parsed = questionnaireDefinitionSchema.safeParse(input);
    if (!parsed.success) {
        return {
            valid: false,
            issues: parsed.error.issues.map((issue) => ({
                code: 'SCHEMA_INVALID',
                path: issue.path.join('.'),
                message: issue.message,
            })),
        };
    }
    const definition = parsed.data;
    const issues = [];
    const sectionByKey = new Map(definition.sections.map((section) => [section.key, section]));
    const questionByKey = new Map();
    const sectionKeyByQuestionKey = new Map();
    const orderByQuestionKey = new Map();
    const graph = new Map();
    const allKeys = new Set();
    const sectionSortOrders = new Set();
    let questionOrder = 0;
    for (const section of [...definition.sections].sort((left, right) => left.sortOrder - right.sortOrder)) {
        if (sectionSortOrders.has(section.sortOrder)) {
            issues.push({
                code: 'QUESTION_CONFIGURATION_INVALID',
                path: `sections.${section.key}.sortOrder`,
                message: `Duplicate section sort order: ${section.sortOrder}`,
            });
        }
        sectionSortOrders.add(section.sortOrder);
        if (allKeys.has(section.key)) {
            issues.push({
                code: 'DUPLICATE_KEY',
                path: `sections.${section.key}`,
                message: `Duplicate section key: ${section.key}`,
            });
        }
        allKeys.add(section.key);
        const questionSortOrders = new Set();
        for (const question of [...section.questions].sort((left, right) => left.sortOrder - right.sortOrder)) {
            if (questionSortOrders.has(question.sortOrder)) {
                issues.push({
                    code: 'QUESTION_CONFIGURATION_INVALID',
                    path: `questions.${question.key}.sortOrder`,
                    message: `Duplicate question sort order in section ${section.key}: ${question.sortOrder}`,
                });
            }
            questionSortOrders.add(question.sortOrder);
            if (allKeys.has(question.key) || questionByKey.has(question.key)) {
                issues.push({
                    code: 'DUPLICATE_KEY',
                    path: `questions.${question.key}`,
                    message: `Duplicate question key: ${question.key}`,
                });
            }
            allKeys.add(question.key);
            questionByKey.set(question.key, question);
            sectionKeyByQuestionKey.set(question.key, section.key);
            orderByQuestionKey.set(question.key, questionOrder);
            graph.set(question.key, new Set());
            questionOrder += 1;
            if (question.internal && question.visibleInCustomerMode) {
                issues.push({
                    code: 'CUSTOMER_MODE_INTERNAL_QUESTION',
                    path: `questions.${question.key}.visibleInCustomerMode`,
                    message: 'Internal questions cannot be visible in customer mode.',
                });
            }
            if (question.analyticsKey !== undefined &&
                !questionnaireAggregatableTypes.has(question.type)) {
                issues.push({
                    code: 'QUESTION_CONFIGURATION_INVALID',
                    path: `questions.${question.key}.analyticsKey`,
                    message: `Analytics are not supported for ${question.type}. Product analytics are derived from relational product answers.`,
                });
            }
            if (question.sensitiveClassification !== 'NONE' &&
                (question.analyticsKey !== undefined || question.searchable)) {
                issues.push({
                    code: 'QUESTION_CONFIGURATION_INVALID',
                    path: `questions.${question.key}.sensitiveClassification`,
                    message: 'Sensitive questions cannot participate in generic search or analytics.',
                });
            }
            if (questionnaireNonAnswerTypes.has(question.type) && question.required) {
                issues.push({
                    code: 'QUESTION_CONFIGURATION_INVALID',
                    path: `questions.${question.key}.required`,
                    message: 'Display-only questions cannot be required.',
                });
            }
            if (question.defaultValue !== undefined) {
                const defaultValidation = validateQuestionnaireAnswer(question, {
                    questionKey: question.key,
                    type: question.type,
                    value: question.defaultValue,
                });
                if (!defaultValidation.valid) {
                    issues.push({
                        code: 'QUESTION_CONFIGURATION_INVALID',
                        path: `questions.${question.key}.defaultValue`,
                        message: `Invalid default value: ${defaultValidation.issues.join(' ')}`,
                    });
                }
            }
        }
    }
    if (questionByKey.size > QUESTIONNAIRE_MAX_QUESTIONS) {
        issues.push({
            code: 'QUESTION_LIMIT_EXCEEDED',
            path: 'sections',
            message: `A questionnaire can contain at most ${QUESTIONNAIRE_MAX_QUESTIONS} questions.`,
        });
    }
    const repeatableOwnerByChildKey = new Map();
    for (const question of questionByKey.values()) {
        if (question.type !== 'REPEATABLE_GROUP')
            continue;
        const childKeys = new Set();
        for (const childKey of question.config.childQuestionKeys) {
            const child = questionByKey.get(childKey);
            const priorOwner = repeatableOwnerByChildKey.get(childKey);
            const invalid = childKeys.has(childKey) ||
                child === undefined ||
                childKey === question.key ||
                (child !== undefined && questionnaireUnsupportedRepeatableChildTypes.has(child.type)) ||
                sectionKeyByQuestionKey.get(childKey) !== sectionKeyByQuestionKey.get(question.key) ||
                (orderByQuestionKey.get(childKey) ?? -1) <= (orderByQuestionKey.get(question.key) ?? -1) ||
                (priorOwner !== undefined && priorOwner !== question.key);
            if (invalid) {
                issues.push({
                    code: 'REPEATABLE_REFERENCE_INVALID',
                    path: `questions.${question.key}.config.childQuestionKeys`,
                    message: `Invalid repeatable child question reference: ${childKey}`,
                });
            }
            else {
                repeatableOwnerByChildKey.set(childKey, question.key);
            }
            childKeys.add(childKey);
        }
    }
    const ruleKeys = new Set();
    const ruleSortOrders = new Set();
    for (const rule of definition.logicRules) {
        if (ruleKeys.has(rule.key) || ruleSortOrders.has(rule.sortOrder)) {
            issues.push({
                code: 'DUPLICATE_KEY',
                path: `logicRules.${rule.key}`,
                message: 'Logic rule keys and sort orders must be unique.',
            });
        }
        ruleKeys.add(rule.key);
        ruleSortOrders.add(rule.sortOrder);
        const targetQuestions = rule.target.kind === 'QUESTION'
            ? [rule.target.key]
            : (sectionByKey.get(rule.target.key)?.questions.map((question) => question.key) ?? []);
        const targetIsCustomerVisible = rule.target.kind === 'SECTION'
            ? sectionByKey.get(rule.target.key)?.visibleInCustomerMode === true
            : (() => {
                const target = questionByKey.get(rule.target.key);
                const targetSectionKey = sectionKeyByQuestionKey.get(rule.target.key);
                return (target !== undefined &&
                    !target.internal &&
                    target.visibleInCustomerMode &&
                    (targetSectionKey === undefined ||
                        sectionByKey.get(targetSectionKey)?.visibleInCustomerMode === true));
            })();
        if (rule.target.kind === 'QUESTION' && repeatableOwnerByChildKey.has(rule.target.key)) {
            issues.push({
                code: 'LOGIC_REFERENCE_INVALID',
                path: `logicRules.${rule.key}.target`,
                message: 'Repeatable child questions cannot be addressed outside their row context.',
            });
        }
        if ((rule.target.kind === 'QUESTION' && !questionByKey.has(rule.target.key)) ||
            (rule.target.kind === 'SECTION' && !sectionByKey.has(rule.target.key))) {
            issues.push({
                code: 'LOGIC_REFERENCE_INVALID',
                path: `logicRules.${rule.key}.target`,
                message: `Unknown ${rule.target.kind.toLowerCase()} target: ${rule.target.key}`,
            });
        }
        for (const condition of rule.conditions) {
            const source = questionByKey.get(condition.sourceQuestionKey);
            if (source === undefined || questionnaireNonAnswerTypes.has(source.type)) {
                issues.push({
                    code: 'LOGIC_REFERENCE_INVALID',
                    path: `logicRules.${rule.key}.conditions`,
                    message: `Unknown or non-answerable source question: ${condition.sourceQuestionKey}`,
                });
                continue;
            }
            if (repeatableOwnerByChildKey.has(condition.sourceQuestionKey)) {
                issues.push({
                    code: 'LOGIC_REFERENCE_INVALID',
                    path: `logicRules.${rule.key}.conditions`,
                    message: 'Repeatable child questions cannot drive global conditional logic.',
                });
                continue;
            }
            const sourceSectionKey = sectionKeyByQuestionKey.get(condition.sourceQuestionKey);
            const sourceIsCustomerVisible = !source.internal &&
                source.visibleInCustomerMode &&
                (sourceSectionKey === undefined ||
                    sectionByKey.get(sourceSectionKey)?.visibleInCustomerMode === true);
            if (targetIsCustomerVisible && !sourceIsCustomerVisible) {
                issues.push({
                    code: 'LOGIC_REFERENCE_INVALID',
                    path: `logicRules.${rule.key}.conditions`,
                    message: 'Customer-visible logic targets may depend only on customer-visible source questions.',
                });
            }
            const numericSource = source.type === 'NUMBER' ||
                source.type === 'DECIMAL' ||
                source.type === 'CURRENCY' ||
                source.type === 'PERCENT' ||
                source.type === 'RATING_SCALE' ||
                source.type === 'STAR_RATING' ||
                source.type === 'SLIDER';
            if ((condition.operator === 'GREATER_THAN' ||
                condition.operator === 'LESS_THAN' ||
                condition.operator === 'BETWEEN') &&
                !numericSource) {
                issues.push({
                    code: 'LOGIC_REFERENCE_INVALID',
                    path: `logicRules.${rule.key}.conditions`,
                    message: `Numeric comparison is not supported for ${source.type}.`,
                });
            }
            if ((source.type === 'SINGLE_CHOICE' ||
                source.type === 'MULTIPLE_CHOICE' ||
                source.type === 'DROPDOWN' ||
                source.type === 'COMBOBOX') &&
                condition.value !== undefined) {
                const allowedValues = new Set(source.config.options.map((option) => option.value));
                const expectedValues = Array.isArray(condition.value) ? condition.value : [condition.value];
                if (expectedValues.some((expected) => typeof expected !== 'string' || !allowedValues.has(expected))) {
                    issues.push({
                        code: 'LOGIC_REFERENCE_INVALID',
                        path: `logicRules.${rule.key}.conditions`,
                        message: 'Logic references an option outside the frozen questionnaire version.',
                    });
                }
            }
            for (const targetQuestionKey of targetQuestions) {
                if (!questionByKey.has(targetQuestionKey))
                    continue;
                graph.get(condition.sourceQuestionKey)?.add(targetQuestionKey);
                const sourceOrder = orderByQuestionKey.get(condition.sourceQuestionKey);
                const targetOrder = orderByQuestionKey.get(targetQuestionKey);
                if (sourceOrder !== undefined && targetOrder !== undefined && sourceOrder >= targetOrder) {
                    issues.push({
                        code: 'UNREACHABLE_REFERENCE',
                        path: `logicRules.${rule.key}`,
                        message: 'Logic may only depend on an earlier reachable question.',
                    });
                }
            }
        }
    }
    const indegree = new Map();
    for (const key of graph.keys())
        indegree.set(key, 0);
    for (const targets of graph.values()) {
        for (const target of targets)
            indegree.set(target, (indegree.get(target) ?? 0) + 1);
    }
    const queue = [...indegree.entries()].filter((entry) => entry[1] === 0).map((entry) => entry[0]);
    let visited = 0;
    while (queue.length > 0) {
        const current = queue.shift();
        if (current === undefined)
            break;
        visited += 1;
        for (const target of graph.get(current) ?? []) {
            const remaining = (indegree.get(target) ?? 0) - 1;
            indegree.set(target, remaining);
            if (remaining === 0)
                queue.push(target);
        }
    }
    if (visited !== graph.size) {
        issues.push({
            code: 'LOGIC_CYCLE',
            path: 'logicRules',
            message: 'Conditional logic contains a cycle.',
        });
    }
    return { valid: issues.length === 0, issues, definition };
}
export const questionnaireAttachmentReferenceSchema = z
    .object({
    attachmentId: idSchema,
    originalName: z.string().trim().min(1).max(255),
    mimeType: z.enum(QUESTIONNAIRE_ALLOWED_FILE_MIME_TYPES),
    fileSize: z.number().int().min(1).max(QUESTIONNAIRE_MAX_FILE_SIZE_BYTES),
    description: questionnaireOptionalTextSchema.optional(),
    capturedAt: isoDateTimeSchema.optional(),
})
    .strict();
export const questionnaireImageAttachmentReferenceSchema = questionnaireAttachmentReferenceSchema.extend({
    mimeType: z.enum(QUESTIONNAIRE_ALLOWED_IMAGE_MIME_TYPES),
});
export const questionnaireSignatureValueSchema = questionnaireImageAttachmentReferenceSchema.extend({
    signerName: z.string().trim().min(1).max(300).optional(),
});
export const questionnaireAddressValueSchema = z
    .object({
    recipient: z.string().trim().max(300).optional(),
    street: z.string().trim().max(300).optional(),
    houseNumber: z.string().trim().max(50).optional(),
    addressLine2: z.string().trim().max(300).optional(),
    postalCode: z.string().trim().max(30).optional(),
    city: z.string().trim().max(200).optional(),
    region: z.string().trim().max(200).optional(),
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/)
        .optional(),
})
    .strict();
export const questionnaireProductAnswerInputSchema = z
    .object({
    productId: idSchema,
    photoAttachmentIds: z
        .array(idSchema)
        .max(QUESTIONNAIRE_MAX_FILES_PER_QUESTION)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: 'Product photo attachment identifiers must be unique.',
    })
        .default([]),
    packagingId: idSchema.optional(),
    interestLevel: questionnaireProductInterestLevelSchema.default('NONE'),
    desiredQuantity: nonNegativeQuantitySchema.optional(),
    plannedPurchaseDate: questionnaireIsoDateSchema.optional(),
    currentCompetitorProduct: z.string().trim().max(500).optional(),
    currentUse: z.boolean().optional(),
    remark: questionnaireOptionalTextSchema.optional(),
    comparisonGroup: z.string().trim().max(100).optional(),
    addedToWatchlist: z.boolean().default(false),
})
    .strict();
export const questionnaireProductAnswerValueSchema = questionnaireProductAnswerInputSchema.extend({
    productNameSnapshot: z.string().trim().min(1).max(500),
    articleNumberSnapshot: z.string().trim().min(1).max(100),
    baseUnitSnapshot: z.string().trim().min(1).max(50),
    packagingNameSnapshot: z.string().trim().max(200).optional(),
    packagingCodeSnapshot: z.string().trim().max(100).optional(),
});
export const questionnaireProductAnswerSchema = questionnaireProductAnswerValueSchema.extend({
    id: idSchema,
    questionKey: questionnaireKeySchema,
    instanceKey: z.string().trim().min(1).max(100),
});
export const questionnaireConsentAnswerInputSchema = z
    .object({
    granted: z.boolean(),
    confirmedByName: z.string().trim().min(1).max(300).optional(),
    signatureAttachmentId: idSchema.optional(),
})
    .strict();
export const questionnaireConsentValueSchema = questionnaireConsentAnswerInputSchema.extend({
    exactText: z.string().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    purpose: z.string().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    documentVersion: z.string().trim().min(1).max(100),
    confirmedAt: isoDateTimeSchema,
    withdrawalNotice: questionnaireOptionalTextSchema.optional(),
});
const questionnaireAnswerBaseSchema = z
    .object({
    questionKey: questionnaireKeySchema,
    instanceKey: z.string().trim().min(1).max(100).default('root'),
    applicability: questionnaireAnswerApplicabilitySchema.default('APPLICABLE'),
})
    .strict();
const questionnaireDecimalAnswerSchema = z
    .string()
    .trim()
    .regex(/^-?\d{1,12}(?:\.\d{1,6})?$/);
export const questionnaireAnswerSchema = z.discriminatedUnion('type', [
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('SHORT_TEXT'),
        value: z.string().max(QUESTIONNAIRE_MAX_SHORT_TEXT_LENGTH).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('LONG_TEXT'),
        value: z.string().max(QUESTIONNAIRE_MAX_TEXT_LENGTH).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('NUMBER'),
        value: z.number().int().safe().nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('DECIMAL'),
        value: questionnaireDecimalAnswerSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('CURRENCY'),
        value: questionnaireDecimalAnswerSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PERCENT'),
        value: questionnaireDecimalAnswerSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('DATE'),
        value: questionnaireIsoDateSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('TIME'),
        value: questionnaireTimeAnswerSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('DATE_TIME'),
        value: isoDateTimeSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('YES_NO'),
        value: z.boolean().nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('CHECKBOX'),
        value: z.boolean().nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('SINGLE_CHOICE'),
        value: z.string().max(200).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('MULTIPLE_CHOICE'),
        value: z.array(z.string().max(200)).max(QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('DROPDOWN'),
        value: z.string().max(200).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('COMBOBOX'),
        value: z.string().max(200).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('RATING_SCALE'),
        value: z.number().finite().nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('STAR_RATING'),
        value: z.number().finite().nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('SLIDER'),
        value: z.number().finite().nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('EMAIL'),
        value: z.string().trim().email().max(320).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PHONE'),
        value: z.string().trim().min(3).max(100).nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('URL'),
        value: z
            .string()
            .trim()
            .url()
            .max(2_048)
            .refine((value) => /^https?:\/\//iu.test(value), 'Only HTTP and HTTPS URLs are supported.')
            .nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('ADDRESS'),
        value: questionnaireAddressValueSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({ type: z.literal('HEADING'), value: z.null() }),
    questionnaireAnswerBaseSchema.extend({ type: z.literal('INFORMATION'), value: z.null() }),
    questionnaireAnswerBaseSchema.extend({ type: z.literal('DIVIDER'), value: z.null() }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('FILE_UPLOAD'),
        value: z
            .array(questionnaireAttachmentReferenceSchema)
            .max(QUESTIONNAIRE_MAX_FILES_PER_QUESTION),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PHOTO'),
        value: z
            .array(questionnaireImageAttachmentReferenceSchema)
            .max(QUESTIONNAIRE_MAX_FILES_PER_QUESTION),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('IMAGE_GALLERY'),
        value: z
            .array(questionnaireImageAttachmentReferenceSchema)
            .max(QUESTIONNAIRE_MAX_FILES_PER_QUESTION),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('SIGNATURE'),
        value: questionnaireSignatureValueSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('CONSENT'),
        value: questionnaireConsentAnswerInputSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PRODUCT_SINGLE'),
        value: questionnaireProductAnswerInputSchema.nullable(),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PRODUCT_MULTI'),
        value: z.array(questionnaireProductAnswerInputSchema).max(500),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PRODUCT_CATALOG'),
        value: z.array(questionnaireProductAnswerInputSchema).max(500),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('PRODUCT_QUANTITY_LIST'),
        value: z.array(questionnaireProductAnswerInputSchema).max(500),
    }),
    questionnaireAnswerBaseSchema.extend({
        type: z.literal('REPEATABLE_GROUP'),
        value: z
            .array(z
            .object({
            instanceKey: z.string().trim().min(1).max(100),
            values: z.record(questionnaireKeySchema, questionnaireLogicValueSchema),
        })
            .strict())
            .max(QUESTIONNAIRE_MAX_REPEATABLE_ROWS),
    }),
]);
/**
 * Request payloads may only submit applicable answers. Applicability is derived
 * from the frozen rule graph by the server and is exposed only on persisted
 * response values, preventing clients from bypassing required or branch rules.
 */
export const questionnaireSubmittedAnswerSchema = questionnaireAnswerSchema.refine((answer) => answer.applicability === 'APPLICABLE', 'Only applicable answers may be submitted; non-applicability is server-authoritative.');
function questionnaireValueIsEmpty(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'string')
        return value.trim().length === 0;
    if (Array.isArray(value))
        return value.length === 0;
    if (typeof value === 'object')
        return Object.values(value).every(questionnaireValueIsEmpty);
    return false;
}
export function validateQuestionnaireAnswer(question, input) {
    const parsed = questionnaireAnswerSchema.safeParse(input);
    if (!parsed.success) {
        return { valid: false, issues: parsed.error.issues.map((issue) => issue.message) };
    }
    const answer = parsed.data;
    const issues = [];
    if (answer.questionKey !== question.key || answer.type !== question.type) {
        issues.push('Answer question key or type does not match the definition.');
    }
    if (question.required &&
        answer.applicability === 'APPLICABLE' &&
        questionnaireValueIsEmpty(answer.value)) {
        issues.push(question.validationMessage ?? 'A required answer is missing.');
    }
    if ((question.type === 'SHORT_TEXT' ||
        question.type === 'LONG_TEXT' ||
        question.type === 'EMAIL' ||
        question.type === 'PHONE' ||
        question.type === 'URL') &&
        typeof answer.value === 'string') {
        const minimum = question.config.minLength;
        const maximum = question.config.maxLength;
        if (minimum !== undefined && answer.value.length < minimum)
            issues.push('The answer is too short.');
        if (maximum !== undefined && answer.value.length > maximum)
            issues.push('The answer is too long.');
        if (question.config.pattern !== undefined &&
            !new RegExp(question.config.pattern, 'u').test(answer.value)) {
            issues.push(question.validationMessage ?? 'The answer does not match the required format.');
        }
    }
    if ((question.type === 'NUMBER' ||
        question.type === 'DECIMAL' ||
        question.type === 'CURRENCY' ||
        question.type === 'PERCENT') &&
        (typeof answer.value === 'number' || typeof answer.value === 'string')) {
        const numericValue = Number(answer.value);
        const minimum = question.type === 'PERCENT' ? (question.config.min ?? 0) : question.config.min;
        const maximum = question.type === 'PERCENT' ? (question.config.max ?? 100) : question.config.max;
        if (minimum !== undefined && numericValue < minimum)
            issues.push('The answer is below the minimum.');
        if (maximum !== undefined && numericValue > maximum)
            issues.push('The answer exceeds the maximum.');
        const fraction = String(answer.value).split('.')[1];
        if (fraction !== undefined && fraction.length > question.config.decimalPlaces) {
            issues.push('The answer has too many decimal places.');
        }
        if (question.config.step !== undefined) {
            const origin = question.config.min ?? 0;
            const stepOffset = (numericValue - origin) / question.config.step;
            if (Math.abs(stepOffset - Math.round(stepOffset)) > Number.EPSILON * 10) {
                issues.push('The answer does not match the configured step.');
            }
        }
    }
    if ((question.type === 'SINGLE_CHOICE' ||
        question.type === 'DROPDOWN' ||
        question.type === 'COMBOBOX') &&
        typeof answer.value === 'string' &&
        !question.config.options.some((option) => option.value === answer.value)) {
        issues.push('The selected option is not part of the questionnaire version.');
    }
    if (question.type === 'MULTIPLE_CHOICE' && answer.type === 'MULTIPLE_CHOICE') {
        const allowedValues = new Set(question.config.options.map((option) => option.value));
        if (answer.value.some((value) => typeof value !== 'string' || !allowedValues.has(value))) {
            issues.push('At least one selected option is not part of the questionnaire version.');
        }
        if (new Set(answer.value).size !== answer.value.length) {
            issues.push('An option may only be selected once.');
        }
        if (question.config.minSelections !== undefined &&
            answer.value.length < question.config.minSelections) {
            issues.push('Too few options were selected.');
        }
        if (question.config.maxSelections !== undefined &&
            answer.value.length > question.config.maxSelections) {
            issues.push('Too many options were selected.');
        }
    }
    if ((question.type === 'RATING_SCALE' ||
        question.type === 'STAR_RATING' ||
        question.type === 'SLIDER') &&
        typeof answer.value === 'number') {
        if (answer.value < question.config.min || answer.value > question.config.max) {
            issues.push('The rating is outside the configured range.');
        }
        const stepOffset = (answer.value - question.config.min) / question.config.step;
        if (Math.abs(stepOffset - Math.round(stepOffset)) > Number.EPSILON * 10) {
            issues.push('The rating does not match the configured step.');
        }
    }
    if ((question.type === 'DATE' || question.type === 'TIME' || question.type === 'DATE_TIME') &&
        typeof answer.value === 'string') {
        if (question.config.min !== undefined && answer.value < question.config.min) {
            issues.push('The date or time is below the configured minimum.');
        }
        if (question.config.max !== undefined && answer.value > question.config.max) {
            issues.push('The date or time exceeds the configured maximum.');
        }
    }
    if (question.type === 'ADDRESS' && answer.type === 'ADDRESS' && answer.value !== null) {
        if (question.config.requireStreet && questionnaireValueIsEmpty(answer.value.street)) {
            issues.push('The street is required.');
        }
        if (question.config.requirePostalCode && questionnaireValueIsEmpty(answer.value.postalCode)) {
            issues.push('The postal code is required.');
        }
        if (question.config.requireCity && questionnaireValueIsEmpty(answer.value.city)) {
            issues.push('The city is required.');
        }
        if (question.config.requireCountry && questionnaireValueIsEmpty(answer.value.countryCode)) {
            issues.push('The country is required.');
        }
    }
    if ((question.type === 'FILE_UPLOAD' && answer.type === 'FILE_UPLOAD') ||
        (question.type === 'PHOTO' && answer.type === 'PHOTO') ||
        (question.type === 'IMAGE_GALLERY' && answer.type === 'IMAGE_GALLERY')) {
        if (answer.value.length > question.config.maxFiles)
            issues.push('Too many files were uploaded.');
        const attachmentIds = new Set();
        for (const attachment of answer.value) {
            if (typeof attachment === 'object' &&
                attachment !== null &&
                'fileSize' in attachment &&
                typeof attachment.fileSize === 'number' &&
                attachment.fileSize > question.config.maxFileSizeBytes) {
                issues.push('An uploaded file exceeds the configured size limit.');
            }
            if (attachmentIds.has(attachment.attachmentId)) {
                issues.push('An uploaded file may only be referenced once.');
            }
            attachmentIds.add(attachment.attachmentId);
            if (!question.config.allowedMimeTypes.some((allowed) => allowed === attachment.mimeType)) {
                issues.push('An uploaded file has a disallowed MIME type.');
            }
            const extension = attachment.originalName.split('.').pop()?.toLowerCase();
            if (extension === undefined ||
                !question.config.allowedExtensions.some((allowed) => allowed === extension)) {
                issues.push('An uploaded file has a disallowed extension.');
            }
            if (question.config.requireDescription && questionnaireValueIsEmpty(attachment.description)) {
                issues.push('An uploaded file requires a description.');
            }
        }
    }
    if (question.type === 'SIGNATURE' && answer.type === 'SIGNATURE' && answer.value !== null) {
        if (question.config.requireName && answer.value.signerName === undefined) {
            issues.push('The signer name is required.');
        }
    }
    if (question.type === 'CONSENT' && answer.type === 'CONSENT' && answer.value !== null) {
        if (question.required && !answer.value.granted)
            issues.push('The required consent was not granted.');
        if (question.config.requireName && answer.value.confirmedByName === undefined) {
            issues.push('The confirming person name is required.');
        }
        if (question.config.requireSignature && answer.value.signatureAttachmentId === undefined) {
            issues.push('A signature is required for this consent.');
        }
    }
    if ((question.type === 'PRODUCT_SINGLE' && answer.type === 'PRODUCT_SINGLE') ||
        (question.type === 'PRODUCT_MULTI' && answer.type === 'PRODUCT_MULTI') ||
        (question.type === 'PRODUCT_CATALOG' && answer.type === 'PRODUCT_CATALOG') ||
        (question.type === 'PRODUCT_QUANTITY_LIST' && answer.type === 'PRODUCT_QUANTITY_LIST')) {
        const products = Array.isArray(answer.value)
            ? answer.value
            : answer.value === null
                ? []
                : [answer.value];
        if (question.config.minSelections !== undefined &&
            products.length < question.config.minSelections) {
            issues.push('Too few products were selected.');
        }
        if (question.config.maxSelections !== undefined &&
            products.length > question.config.maxSelections) {
            issues.push('Too many products were selected.');
        }
        const productIds = products.map((product) => product.productId);
        if (new Set(productIds).size !== productIds.length) {
            issues.push('A product may only be selected once.');
        }
        if (question.config.filters.productIds.length > 0 &&
            productIds.some((productId) => !question.config.filters.productIds.includes(productId))) {
            issues.push('A selected product is outside the configured product filter.');
        }
        for (const product of products) {
            const display = question.config.display;
            if ((product.packagingId !== undefined && !display.showPackaging) ||
                (product.desiredQuantity !== undefined && !display.allowQuantity) ||
                (product.interestLevel !== 'NONE' && !display.allowInterest) ||
                (product.currentUse !== undefined && !display.allowCurrentUse) ||
                (product.plannedPurchaseDate !== undefined && !display.allowPlannedPurchaseDate) ||
                (product.currentCompetitorProduct !== undefined && !display.allowCompetitorProduct) ||
                (product.remark !== undefined && !display.allowRemark) ||
                (product.comparisonGroup !== undefined && !display.allowComparison) ||
                (product.photoAttachmentIds.length > 0 && !display.allowProductPhoto) ||
                (product.addedToWatchlist && !display.allowWatchlist)) {
                issues.push('A product answer contains a field that is not enabled by the question.');
            }
        }
    }
    if (question.type === 'REPEATABLE_GROUP' &&
        answer.type === 'REPEATABLE_GROUP' &&
        Array.isArray(answer.value)) {
        if (answer.value.length < question.config.minRows)
            issues.push('Too few rows were added.');
        if (answer.value.length > question.config.maxRows)
            issues.push('Too many rows were added.');
        const instanceKeys = answer.value.map((row) => row.instanceKey);
        if (new Set(instanceKeys).size !== instanceKeys.length) {
            issues.push('A repeatable row identifier may only be used once.');
        }
        const allowedChildKeys = new Set(question.config.childQuestionKeys);
        if (answer.value.some((row) => Object.keys(row.values).some((key) => !allowedChildKeys.has(key)))) {
            issues.push('A repeatable row contains an unknown child question.');
        }
    }
    return { valid: issues.length === 0, issues, answer };
}
function questionnaireComparableValue(answer) {
    if (answer === undefined ||
        (answer.applicability !== undefined && answer.applicability !== 'APPLICABLE'))
        return undefined;
    if (answer.type === 'PRODUCT_SINGLE')
        return answer.value?.productId;
    if (answer.type === 'PRODUCT_MULTI' ||
        answer.type === 'PRODUCT_CATALOG' ||
        answer.type === 'PRODUCT_QUANTITY_LIST') {
        return answer.value.map((product) => product.productId);
    }
    if (answer.type === 'CONSENT')
        return answer.value?.granted;
    return answer.value;
}
function questionnaireValuesEqual(left, right) {
    if (Array.isArray(left) && Array.isArray(right)) {
        return (left.length === right.length &&
            left.every((entry, index) => questionnaireValuesEqual(entry, right[index])));
    }
    return Object.is(left, right);
}
function questionnaireArrayIncludes(haystack, needle) {
    return haystack.some((entry) => questionnaireValuesEqual(entry, needle));
}
function questionnaireConditionMatches(value, condition) {
    const expected = condition.value;
    switch (condition.operator) {
        case 'EMPTY':
            return questionnaireValueIsEmpty(value);
        case 'NOT_EMPTY':
            return !questionnaireValueIsEmpty(value);
        case 'EQUALS':
            return questionnaireValuesEqual(value, expected);
        case 'NOT_EQUALS':
            return !questionnaireValuesEqual(value, expected);
        case 'CONTAINS':
            if (typeof value === 'string' && typeof expected === 'string')
                return value.includes(expected);
            if (Array.isArray(value))
                return questionnaireArrayIncludes(value, expected);
            return false;
        case 'NOT_CONTAINS':
            if (typeof value === 'string' && typeof expected === 'string')
                return !value.includes(expected);
            if (Array.isArray(value))
                return !questionnaireArrayIncludes(value, expected);
            return true;
        case 'GREATER_THAN':
            return (typeof value === 'number' || typeof value === 'string') &&
                (typeof expected === 'number' || typeof expected === 'string')
                ? Number(value) > Number(expected)
                : false;
        case 'LESS_THAN':
            return (typeof value === 'number' || typeof value === 'string') &&
                (typeof expected === 'number' || typeof expected === 'string')
                ? Number(value) < Number(expected)
                : false;
        case 'ONE_OF':
            return Array.isArray(expected) && Array.isArray(value)
                ? value.some((entry) => questionnaireArrayIncludes(expected, entry))
                : Array.isArray(expected) && questionnaireArrayIncludes(expected, value);
        case 'ALL_OF':
            return Array.isArray(value) && Array.isArray(expected)
                ? expected.every((entry) => questionnaireArrayIncludes(value, entry))
                : false;
        case 'BETWEEN':
            if (!Array.isArray(expected) || expected.length !== 2)
                return false;
            if (typeof value !== 'number' && typeof value !== 'string')
                return false;
            return Number(value) >= Number(expected[0]) && Number(value) <= Number(expected[1]);
    }
}
export function evaluateQuestionnaireVisibility(definition, answers) {
    const answerByKey = new Map(answers.map((answer) => [answer.questionKey, answer]));
    const visibility = {};
    const sectionQuestions = new Map();
    const orderedSections = [...definition.sections].sort((left, right) => left.sortOrder - right.sortOrder);
    for (const section of definition.sections) {
        visibility[section.key] = section.visibleInCustomerMode;
        sectionQuestions.set(section.key, section.questions.map((question) => question.key));
        for (const question of section.questions) {
            visibility[question.key] =
                section.visibleInCustomerMode && !question.internal && question.visibleInCustomerMode;
        }
    }
    for (const rule of definition.logicRules) {
        if (rule.action !== 'SHOW')
            continue;
        const targets = rule.target.kind === 'QUESTION'
            ? [rule.target.key]
            : [rule.target.key, ...(sectionQuestions.get(rule.target.key) ?? [])];
        for (const target of targets)
            visibility[target] = false;
    }
    for (const rule of [...definition.logicRules].sort((left, right) => left.sortOrder - right.sortOrder)) {
        const results = rule.conditions.map((condition) => questionnaireConditionMatches(questionnaireComparableValue(answerByKey.get(condition.sourceQuestionKey)), condition));
        const matched = rule.combinator === 'AND' ? results.every(Boolean) : results.some(Boolean);
        if (!matched)
            continue;
        if (rule.action === 'SKIP_TO_SECTION' && rule.target.kind === 'SECTION') {
            const targetIndex = orderedSections.findIndex((section) => section.key === rule.target.key);
            const sourceIndexes = rule.conditions.flatMap((condition) => {
                const index = orderedSections.findIndex((section) => section.questions.some((question) => question.key === condition.sourceQuestionKey));
                return index < 0 ? [] : [index];
            });
            const sourceIndex = sourceIndexes.length === 0 ? -1 : Math.max(...sourceIndexes);
            if (sourceIndex >= 0 && targetIndex > sourceIndex) {
                for (let index = sourceIndex + 1; index < targetIndex; index += 1) {
                    const skipped = orderedSections[index];
                    if (skipped === undefined)
                        continue;
                    visibility[skipped.key] = false;
                    for (const question of skipped.questions)
                        visibility[question.key] = false;
                }
            }
            continue;
        }
        const targets = rule.target.kind === 'QUESTION'
            ? [rule.target.key]
            : [rule.target.key, ...(sectionQuestions.get(rule.target.key) ?? [])];
        const visible = rule.action === 'SHOW';
        for (const target of targets)
            visibility[target] = visible;
    }
    for (const section of definition.sections) {
        if (visibility[section.key] === false) {
            for (const question of section.questions)
                visibility[question.key] = false;
        }
    }
    return visibility;
}
export const questionnaireTemplateCreateSchema = z
    .object({
    internalName: z.string().trim().min(1).max(200),
    definition: questionnaireDefinitionSchema,
})
    .strict();
export const questionnaireTemplateUpdateSchema = z
    .object({
    versionId: idSchema,
    internalName: z.string().trim().min(1).max(200).optional(),
    lockVersion: z.number().int().min(1),
    definition: questionnaireDefinitionSchema.optional(),
})
    .strict();
export const questionnaireTemplateVersionCreateSchema = z
    .object({
    sourceVersionId: idSchema.optional(),
    definition: questionnaireDefinitionSchema.optional(),
})
    .strict()
    .refine((value) => value.sourceVersionId !== undefined || value.definition !== undefined, {
    message: 'A source version or definition is required.',
});
export const questionnaireTemplateVersionSchema = z
    .object({
    id: idSchema,
    templateId: idSchema,
    versionNumber: z.number().int().min(1),
    status: questionnaireTemplateStatusSchema,
    lockVersion: z.number().int().min(1),
    definition: questionnaireDefinitionSchema,
    createdByUserId: idSchema,
    updatedByUserId: idSchema,
    publishedByUserId: idSchema.nullable(),
    archivedByUserId: idSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    publishedAt: isoDateTimeSchema.nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
})
    .strict();
export const questionnaireTemplateSchema = z
    .object({
    id: idSchema,
    internalName: z.string(),
    status: questionnaireTemplateStatusSchema,
    title: z.string(),
    description: z.string().nullable(),
    applicationScopes: z.array(questionnaireApplicationScopeSchema),
    latestVersionNumber: z.number().int().min(1),
    publishedVersionId: idSchema.nullable(),
    publishedVersionNumber: z.number().int().min(1).nullable(),
    draftVersionNumber: z.number().int().min(1).nullable(),
    createdByUserId: idSchema,
    updatedByUserId: idSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    deletedAt: isoDateTimeSchema.nullable(),
})
    .strict();
export const questionnaireTemplateDetailSchema = z
    .object({
    template: questionnaireTemplateSchema,
    versions: z.array(questionnaireTemplateVersionSchema),
})
    .strict();
export const questionnaireTemplateListQuerySchema = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(QUESTIONNAIRE_PAGE_SIZE_MAX)
        .default(QUESTIONNAIRE_PAGE_SIZE_DEFAULT),
    q: z.string().trim().min(1).max(200).optional(),
    status: questionnaireTemplateStatusSchema.optional(),
    applicationScope: questionnaireApplicationScopeSchema.optional(),
    sort: z
        .enum(['updatedAt:desc', 'updatedAt:asc', 'internalName:asc', 'internalName:desc'])
        .default('updatedAt:desc'),
})
    .strict();
export const questionnaireTemplateListResponseSchema = z.object({
    data: z.array(questionnaireTemplateSchema),
    meta: paginationMetaSchema,
});
export const questionnaireTemplateValidateResponseSchema = z.object({
    valid: z.boolean(),
    issues: z.array(z
        .object({
        code: z.string(),
        path: z.string(),
        message: z.string(),
    })
        .strict()),
});
export const questionnaireTemplatePublishSchema = z
    .object({
    versionId: idSchema,
    lockVersion: z.number().int().min(1),
})
    .strict();
export const questionnaireTemplatePublishResponseSchema = z
    .object({
    templateId: idSchema,
    versionId: idSchema,
    versionNumber: z.number().int().min(1),
    status: z.literal('PUBLISHED'),
    publishedAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireTemplateArchiveSchema = z
    .object({
    versionId: idSchema,
    lockVersion: z.number().int().min(1),
})
    .strict();
export const questionnaireTemplateDuplicateSchema = z
    .object({
    internalName: z.string().trim().min(1).max(200),
    sourceVersionId: idSchema.optional(),
})
    .strict();
export const questionnairePartnerlessDataSchema = z
    .object({
    company: z.string().trim().max(300).optional(),
    firstName: z.string().trim().max(200).optional(),
    lastName: z.string().trim().max(200).optional(),
    email: z.string().trim().email().max(320).optional(),
    phone: z.string().trim().max(100).optional(),
    street: z.string().trim().max(300).optional(),
    postalCode: z.string().trim().max(30).optional(),
    city: z.string().trim().max(200).optional(),
    countryCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/)
        .optional(),
    source: z.string().trim().max(200).optional(),
    note: questionnaireOptionalTextSchema.optional(),
    contactConsent: z.boolean().optional(),
})
    .strict();
export const questionnaireRunCreateSchema = z
    .object({
    templateVersionId: idSchema,
    partnerId: idSchema.optional(),
    contactId: idSchema.optional(),
    clientRunId: z.string().uuid().optional(),
    partnerless: questionnairePartnerlessDataSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.contactId !== undefined && value.partnerId === undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['contactId'],
            message: 'A contact requires a partner.',
        });
    }
    if (value.partnerId !== undefined && value.partnerless !== undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['partnerless'],
            message: 'Partner and partnerless data are mutually exclusive.',
        });
    }
});
export const questionnaireRunSchema = z
    .object({
    id: idSchema,
    clientRunId: z.string().uuid().nullable(),
    templateId: idSchema,
    versionId: idSchema,
    versionNumber: z.number().int().min(1),
    templateTitle: z.string(),
    status: questionnaireRunStatusSchema,
    syncStatus: questionnaireSyncStatusSchema,
    postProcessingStatus: questionnairePostProcessingStatusSchema,
    ownerUserId: idSchema,
    ownerName: z.string(),
    partnerId: idSchema.nullable(),
    partnerName: z.string().nullable(),
    contactId: idSchema.nullable(),
    contactName: z.string().nullable(),
    partnerless: questionnairePartnerlessDataSchema.nullable(),
    currentSectionKey: z.string().nullable(),
    currentQuestionKey: z.string().nullable(),
    answeredQuestionCount: z.number().int().min(0),
    totalQuestionCount: z.number().int().min(0),
    progressPercent: z.number().int().min(0).max(100),
    durationSeconds: z.number().int().min(0).nullable(),
    lockVersion: z.number().int().min(1),
    latestRevisionNumber: z.number().int().min(0),
    startedAt: isoDateTimeSchema.nullable(),
    pausedAt: isoDateTimeSchema.nullable(),
    completedAt: isoDateTimeSchema.nullable(),
    abandonedAt: isoDateTimeSchema.nullable(),
    lastSavedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireAnswerFilterSchema = z
    .object({
    analyticsKey: questionnaireKeySchema,
    operator: z.enum(QUESTIONNAIRE_LOGIC_OPERATORS),
    value: questionnaireLogicValueSchema.optional(),
})
    .strict()
    .superRefine((filter, context) => {
    const condition = questionnaireLogicConditionSchema.safeParse({
        sourceQuestionKey: filter.analyticsKey,
        operator: filter.operator,
        ...(filter.value === undefined ? {} : { value: filter.value }),
    });
    if (!condition.success) {
        for (const issue of condition.error.issues) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: issue.path[0] === 'value' ? ['value'] : [],
                message: issue.message,
            });
        }
    }
});
export const questionnaireRunListQuerySchema = z
    .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
        .number()
        .int()
        .min(1)
        .max(QUESTIONNAIRE_PAGE_SIZE_MAX)
        .default(QUESTIONNAIRE_PAGE_SIZE_DEFAULT),
    q: z.string().trim().min(1).max(200).optional(),
    templateId: idSchema.optional(),
    versionNumber: z.coerce.number().int().min(1).optional(),
    status: questionnaireRunStatusSchema.optional(),
    syncStatus: questionnaireSyncStatusSchema.optional(),
    syncStatuses: z.preprocess((value) => (typeof value === 'string' ? [value] : value), z
        .array(questionnaireSyncStatusSchema)
        .min(1)
        .max(QUESTIONNAIRE_SYNC_STATUSES.length)
        .optional()),
    postProcessingStatus: questionnairePostProcessingStatusSchema.optional(),
    ownerUserId: idSchema.optional(),
    partnerId: idSchema.optional(),
    partnerRole: z.enum(['CUSTOMER', 'PROSPECT', 'SUPPLIER']).optional(),
    partnerless: z.preprocess((value) => (value === 'true' ? true : value === 'false' ? false : value), z.boolean().optional()),
    complete: z.preprocess((value) => (value === 'true' ? true : value === 'false' ? false : value), z.boolean().optional()),
    productId: idSchema.optional(),
    productCategoryId: idSchema.optional(),
    answerFilter: questionnaireAnswerFilterSchema.optional(),
    dateFrom: isoDateTimeSchema.optional(),
    dateTo: isoDateTimeSchema.optional(),
    sort: z
        .enum(['createdAt:desc', 'createdAt:asc', 'updatedAt:desc', 'completedAt:desc'])
        .default('createdAt:desc'),
})
    .strict();
export const questionnaireRunListItemSchema = questionnaireRunSchema.extend({
    attachmentCount: z.number().int().min(0),
    photoCount: z.number().int().min(0),
    productAnswerCount: z.number().int().min(0),
});
export const questionnaireRunListResponseSchema = z.object({
    data: z.array(questionnaireRunListItemSchema),
    meta: paginationMetaSchema,
});
export const questionnaireAnswersPatchSchema = z
    .object({
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
    lockVersion: z.number().int().min(1),
    clientEventId: z.string().uuid(),
    idempotencyKey: z.string().uuid(),
})
    .strict();
export const questionnaireRunTransitionSchema = z
    .object({ lockVersion: z.number().int().min(1) })
    .strict();
export const questionnaireRunCompleteSchema = questionnaireRunTransitionSchema.extend({
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS).optional(),
});
export const questionnaireRunLinkPartnerSchema = z
    .object({
    partnerId: idSchema,
    contactId: idSchema.optional(),
    lockVersion: z.number().int().min(1),
})
    .strict();
export const questionnairePartnerConversionCreateSchema = z
    .object({
    targetRole: questionnairePartnerConversionTargetRoleSchema,
    confirmed: z.literal(true),
    duplicatesAcknowledged: z.boolean(),
    lockVersion: z.number().int().min(1),
})
    .strict();
export const questionnairePartnerConversionSchema = z
    .object({
    id: idSchema,
    runId: idSchema,
    targetRole: questionnairePartnerConversionTargetRoleSchema,
    partnerId: idSchema.nullable(),
    status: questionnairePartnerConversionStatusSchema,
    errorCode: z.string().trim().min(1).max(200).nullable(),
    errorMessage: questionnaireOptionalTextSchema.nullable(),
    confirmedByUserId: idSchema,
    duplicatesAcknowledged: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((conversion, context) => {
    const hasPartner = conversion.partnerId !== null;
    const hasError = conversion.errorCode !== null || conversion.errorMessage !== null;
    if (conversion.status === 'SUCCEEDED' && (!hasPartner || hasError)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A successful conversion requires a partner and must not contain an error.',
        });
    }
    if (conversion.status === 'PROCESSING' && (hasPartner || hasError)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A processing conversion must not contain a result or an error.',
        });
    }
    if (conversion.status === 'FAILED' && (hasPartner || conversion.errorCode === null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A failed conversion requires an error code and must not contain a partner.',
        });
    }
});
export const questionnaireConvertedPartnerSchema = z
    .object({
    id: idSchema,
    displayName: z.string().trim().min(1).max(500),
    isCustomer: z.boolean(),
    isProspect: z.boolean(),
    isSupplier: z.boolean(),
})
    .strict();
export const questionnairePartnerConversionResponseSchema = z
    .object({
    conversion: questionnairePartnerConversionSchema,
    partner: questionnaireConvertedPartnerSchema.nullable(),
})
    .strict()
    .superRefine((response, context) => {
    if ((response.conversion.partnerId === null && response.partner !== null) ||
        (response.conversion.partnerId !== null &&
            response.partner?.id !== response.conversion.partnerId)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['partner'],
            message: 'The converted partner must match the conversion result.',
        });
    }
    if (response.conversion.status === 'SUCCEEDED' &&
        response.partner !== null &&
        ((response.conversion.targetRole === 'CUSTOMER' && !response.partner.isCustomer) ||
            (response.conversion.targetRole === 'PROSPECT' && !response.partner.isProspect))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['partner'],
            message: 'The converted partner does not have the confirmed target role.',
        });
    }
});
export const questionnaireAttachmentSchema = questionnaireAttachmentReferenceSchema.extend({
    id: idSchema,
    questionKey: questionnaireKeySchema,
    productId: idSchema.nullable(),
    processingStatus: questionnaireAttachmentProcessingStatusSchema,
    processingError: z.string().nullable(),
    createdAt: isoDateTimeSchema,
});
export const questionnaireAttachmentUploadMetadataSchema = z
    .object({
    questionKey: questionnaireKeySchema,
    productId: idSchema.optional(),
    clientUploadId: z.string().uuid(),
    description: questionnaireOptionalTextSchema.optional(),
    capturedAt: isoDateTimeSchema.optional(),
})
    .strict();
export const questionnaireAttachmentUploadResponseSchema = z
    .object({
    attachment: questionnaireAttachmentSchema,
    idempotentReplay: z.boolean(),
})
    .strict();
export const questionnaireConsentSchema = questionnaireConsentValueSchema.extend({
    id: idSchema,
    questionKey: questionnaireKeySchema,
    isRequired: z.boolean(),
    recordedByUserId: idSchema,
    revokedAt: isoDateTimeSchema.nullable(),
    revokedByUserId: idSchema.nullable(),
    revocationReason: z.string().nullable(),
});
export const questionnaireRunRevisionSchema = z
    .object({
    id: idSchema,
    runId: idSchema,
    revisionNumber: z.number().int().min(1),
    baseRevisionNumber: z.number().int().min(0),
    reason: z.string().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    createdByUserId: idSchema,
    createdAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireCustomerSessionCreateSchema = z
    .object({
    lockVersion: z.number().int().min(1),
    fullscreenRequested: z.boolean().default(false),
})
    .strict();
export const questionnaireCustomerSessionSchema = z
    .object({
    runId: idSchema,
    expiresAt: isoDateTimeSchema,
    lastActivityAt: isoDateTimeSchema,
    idleLockMinutes: z.number().int().min(1).max(60),
    /** Server-authoritative tenant/template policy snapshot for this handoff. */
    secureOfflineAllowed: z.boolean(),
})
    .strict();
const questionnaireCustomerQuestionOptionSchema = questionnaireQuestionOptionSchema.omit({
    id: true,
});
const questionnaireCustomerChoiceConfigSchema = z
    .object({
    options: z
        .array(questionnaireCustomerQuestionOptionSchema)
        .min(1)
        .max(QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION),
    randomizeOptions: z.boolean().default(false),
    minSelections: z.number().int().min(0).optional(),
    maxSelections: z.number().int().min(1).optional(),
    searchable: z.boolean().default(false),
})
    .strict()
    .superRefine((value, context) => {
    const keys = new Set();
    const optionValues = new Set();
    for (const option of value.options) {
        if (keys.has(option.key) ||
            optionValues.has(option.value) ||
            value.options.some((candidate) => candidate !== option && candidate.sortOrder === option.sortOrder)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['options'],
                message: 'Option keys, values and sort orders must be unique.',
            });
        }
        keys.add(option.key);
        optionValues.add(option.value);
    }
    if (value.minSelections !== undefined &&
        value.maxSelections !== undefined &&
        value.minSelections > value.maxSelections) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxSelections'],
            message: 'maxSelections must be greater than or equal to minSelections.',
        });
    }
    if (value.maxSelections !== undefined && value.maxSelections > value.options.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['maxSelections'],
            message: 'maxSelections cannot exceed the number of options.',
        });
    }
});
const questionnaireCustomerProductConfigSchema = z
    .object({
    display: questionnaireProductDisplaySchema,
    minSelections: z.number().int().min(0).optional(),
    maxSelections: z.number().int().min(1).max(500).optional(),
})
    .strict()
    .refine((value) => value.minSelections === undefined ||
    value.maxSelections === undefined ||
    value.minSelections <= value.maxSelections, { path: ['maxSelections'], message: 'maxSelections must not be below minSelections.' });
const questionnaireCustomerQuestionBaseSchema = questionnaireQuestionBaseSchema.omit({
    id: true,
    visibleInCustomerMode: true,
    internal: true,
    prefillPartnerField: true,
    erpTargetField: true,
    analyticsKey: true,
    searchable: true,
    sensitiveClassification: true,
});
export const questionnaireCustomerQuestionSchema = z.discriminatedUnion('type', [
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('SHORT_TEXT'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('LONG_TEXT'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('NUMBER'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('DECIMAL'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('CURRENCY'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PERCENT'),
        config: questionnaireNumberConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('DATE'),
        config: questionnaireDateConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('TIME'),
        config: questionnaireTimeConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('DATE_TIME'),
        config: questionnaireDateTimeConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('YES_NO'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('CHECKBOX'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('SINGLE_CHOICE'),
        config: questionnaireCustomerChoiceConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('MULTIPLE_CHOICE'),
        config: questionnaireCustomerChoiceConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('DROPDOWN'),
        config: questionnaireCustomerChoiceConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('COMBOBOX'),
        config: questionnaireCustomerChoiceConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('RATING_SCALE'),
        config: questionnaireRatingConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('STAR_RATING'),
        config: questionnaireRatingConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('SLIDER'),
        config: questionnaireRatingConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('EMAIL'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PHONE'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('URL'),
        config: questionnaireTextConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('ADDRESS'),
        config: questionnaireAddressConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('HEADING'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('INFORMATION'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('DIVIDER'),
        config: questionnaireEmptyConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('FILE_UPLOAD'),
        config: questionnaireFileConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PHOTO'),
        config: questionnaireImageConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('IMAGE_GALLERY'),
        config: questionnaireImageConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('SIGNATURE'),
        config: z.object({ requireName: z.boolean().default(false) }).strict(),
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('CONSENT'),
        config: questionnaireConsentConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_SINGLE'),
        config: questionnaireCustomerProductConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_MULTI'),
        config: questionnaireCustomerProductConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_CATALOG'),
        config: questionnaireCustomerProductConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('PRODUCT_QUANTITY_LIST'),
        config: questionnaireCustomerProductConfigSchema,
    }),
    questionnaireCustomerQuestionBaseSchema.extend({
        type: z.literal('REPEATABLE_GROUP'),
        config: questionnaireRepeatableGroupConfigSchema,
    }),
]);
export const questionnaireCustomerSectionSchema = z
    .object({
    key: questionnaireKeySchema,
    title: questionnaireLabelSchema,
    description: questionnaireOptionalTextSchema.optional(),
    translations: questionnaireTranslationsSchema.default({}),
    sortOrder: z.number().int().min(0),
    pageBreakBefore: z.boolean().default(false),
    isCompletionSection: z.boolean().default(false),
    questions: z
        .array(questionnaireCustomerQuestionSchema)
        .max(QUESTIONNAIRE_MAX_QUESTIONS_PER_SECTION),
})
    .strict();
const questionnaireCustomerLogicRuleSchema = z
    .object({
    key: questionnaireKeySchema,
    combinator: z.enum(QUESTIONNAIRE_LOGIC_COMBINATORS),
    action: z.enum(QUESTIONNAIRE_LOGIC_ACTIONS),
    target: questionnaireLogicTargetSchema,
    conditions: z
        .array(questionnaireLogicConditionSchema)
        .min(1)
        .max(QUESTIONNAIRE_MAX_CONDITIONS_PER_RULE),
    sortOrder: z.number().int().min(0),
})
    .strict()
    .superRefine((rule, context) => {
    if (rule.action === 'SKIP_TO_SECTION' && rule.target.kind !== 'SECTION') {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['target'],
            message: 'SKIP_TO_SECTION requires a section target.',
        });
    }
});
/**
 * Deliberately minimal definition delivered to an untrusted customer-mode client.
 * ERP mappings, analytics metadata, DMS ids and product selection filters are not
 * members of this schema and are rejected instead of silently accepted.
 */
export const questionnaireCustomerDefinitionSchema = z
    .object({
    title: questionnaireLabelSchema,
    description: questionnaireOptionalTextSchema.optional(),
    language: questionnaireLocaleSchema.default('de'),
    translations: questionnaireTranslationsSchema.default({}),
    greetingText: questionnaireOptionalTextSchema.optional(),
    completionText: questionnaireOptionalTextSchema.optional(),
    privacyText: questionnaireOptionalTextSchema.optional(),
    customerMode: questionnaireCustomerModeConfigSchema.omit({ enabled: true }),
    offline: questionnaireOfflineConfigSchema.pick({ enabled: true }),
    presentation: questionnairePresentationConfigSchema.omit({ hiddenAnswerPolicy: true }),
    sections: z.array(questionnaireCustomerSectionSchema).min(1).max(QUESTIONNAIRE_MAX_SECTIONS),
    logicRules: z.array(questionnaireCustomerLogicRuleSchema).max(QUESTIONNAIRE_MAX_LOGIC_RULES),
})
    .strict()
    .superRefine((definition, context) => {
    const questions = definition.sections.flatMap((section) => section.questions);
    const questionKeys = new Set(questions.map((question) => question.key));
    questions.forEach((question, index) => {
        if (question.type === 'REPEATABLE_GROUP' &&
            question.config.childQuestionKeys.some((childKey) => !questionKeys.has(childKey))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['questions', index, 'config', 'childQuestionKeys'],
                message: 'Customer repeatable groups may reference only delivered questions.',
            });
        }
    });
});
export const questionnaireCustomerActiveRunSchema = z
    .object({
    mode: z.literal('ACTIVE'),
    runId: idSchema,
    clientRunId: z.string().uuid(),
    tenantName: z.string(),
    tenantLogoDataUrl: z.string().nullable(),
    titleImageUrl: z.literal('/api/v1/questionnaire-customer/title-image').nullable(),
    title: z.string(),
    definition: questionnaireCustomerDefinitionSchema,
    answers: z.array(questionnaireAnswerSchema),
    secureOfflineAllowed: z.boolean(),
    status: z.enum(['DRAFT', 'IN_PROGRESS', 'PAUSED', 'SYNC_PENDING', 'SYNC_FAILED']),
    progressPercent: z.number().int().min(0).max(100),
    lockVersion: z.number().int().min(1),
    navigation: z
        .object({
        currentSectionKey: questionnaireKeySchema,
        currentQuestionKey: questionnaireKeySchema.nullable(),
        currentIndex: z.number().int().min(0),
        totalSections: z.number().int().min(1),
        totalPages: z.number().int().min(1),
        previousSectionKey: questionnaireKeySchema.nullable(),
        previousQuestionKey: questionnaireKeySchema.nullable(),
        nextSectionKey: questionnaireKeySchema.nullable(),
        nextQuestionKey: questionnaireKeySchema.nullable(),
    })
        .strict(),
    expiresAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireCustomerCompletedRunSchema = z
    .object({
    mode: z.literal('COMPLETED'),
    runId: idSchema,
    title: z.string().trim().min(1).max(500),
    status: z.literal('COMPLETED'),
    completionText: questionnaireOptionalTextSchema.nullable(),
    expiresAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireCustomerRunSchema = z.discriminatedUnion('mode', [
    questionnaireCustomerActiveRunSchema,
    questionnaireCustomerCompletedRunSchema,
]);
export const questionnaireCustomerAnswersPatchSchema = questionnaireAnswersPatchSchema
    .extend({
    /** The page from which the submitted answers originated. */
    sectionKey: questionnaireKeySchema,
    questionKey: questionnaireKeySchema.optional(),
    /** Optional adjacent visible page requested after the atomic save. */
    nextSectionKey: questionnaireKeySchema.optional(),
    nextQuestionKey: questionnaireKeySchema.optional(),
})
    .superRefine((value, context) => {
    if (value.nextQuestionKey !== undefined && value.nextSectionKey === undefined) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['nextQuestionKey'],
            message: 'A nextQuestionKey requires its nextSectionKey.',
        });
    }
});
export const questionnaireCustomerCompleteSchema = questionnaireRunCompleteSchema;
export const questionnaireCustomerUnlockSchema = z
    .object({
    password: z.string().min(1).max(1_024),
    twoFactorCode: z
        .string()
        .regex(/^\d{6}$/)
        .optional(),
})
    .strict();
export const questionnaireCustomerUnlockResponseSchema = z
    .object({
    unlocked: z.literal(true),
    runId: idSchema,
})
    .strict();
export const questionnaireProductOptionQuerySchema = z
    .object({
    questionKey: questionnaireKeySchema,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    q: z.string().trim().min(1).max(200).optional(),
    categoryId: idSchema.optional(),
})
    .strict();
export const questionnaireProductOptionSchema = z
    .object({
    id: idSchema,
    articleNumber: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    baseUnit: z.string().nullable().optional(),
    availableQuantity: nonNegativeQuantitySchema.nullable().optional(),
    categories: z
        .array(z
        .object({
        id: idSchema,
        name: z.string(),
        parentId: idSchema.nullable(),
    })
        .strict())
        .nullable()
        .optional(),
    tags: z.array(z.string()).nullable().optional(),
    packagings: z
        .array(z
        .object({
        id: idSchema,
        code: z.string(),
        name: z.string(),
    })
        .strict())
        .nullable()
        .optional(),
    primaryImage: z
        .object({
        id: idSchema,
        altText: z.string(),
    })
        .strict()
        .nullable()
        .optional(),
    price: z
        .object({
        amount: moneySchema,
        currency: currencyCodeSchema,
    })
        .strict()
        .nullable(),
})
    .strict();
export const questionnaireProductOptionsResponseSchema = z
    .object({
    data: z.array(questionnaireProductOptionSchema),
    meta: paginationMetaSchema,
})
    .strict();
export const questionnaireCompletionActionPayloadSchema = z
    .object({
    productAnswerIds: z.array(idSchema).max(500).default([]),
    attachmentIds: z.array(idSchema).max(100).default([]),
    note: questionnaireOptionalTextSchema.optional(),
    dueAt: isoDateTimeSchema.optional(),
    contact: z
        .object({
        firstName: z.string().trim().min(1).max(200),
        lastName: z.string().trim().min(1).max(200),
        email: z.string().email().optional(),
        phone: z.string().max(100).optional(),
    })
        .strict()
        .optional(),
    leadStatus: z
        .enum(['NEW', 'CONTACTED', 'QUALIFIED', 'PROPOSAL_SENT', 'NEGOTIATION', 'WON', 'LOST'])
        .optional(),
})
    .strict();
export const questionnaireCompletionActionCreateSchema = z
    .object({
    type: questionnaireCompletionActionTypeSchema,
    idempotencyKey: z.string().uuid(),
    confirmed: z.literal(true),
    payload: questionnaireCompletionActionPayloadSchema,
})
    .strict();
export const questionnaireCompletionActionSchema = z
    .object({
    id: idSchema,
    runId: idSchema,
    type: questionnaireCompletionActionTypeSchema,
    status: questionnaireCompletionActionStatusSchema,
    idempotencyKey: z.string().uuid(),
    targetEntityType: z.string().nullable(),
    targetEntityId: idSchema.nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    confirmedByUserId: idSchema,
    confirmedAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireSyncEventInputSchema = z
    .object({
    clientEventId: z.string().uuid(),
    idempotencyKey: z.string().uuid(),
    type: questionnaireSyncEventTypeSchema,
    payloadHash: z.string().regex(/^[a-f0-9]{64}$/u),
})
    .strict();
export const questionnaireSyncEventSchema = questionnaireSyncEventInputSchema.extend({
    id: idSchema,
    runId: idSchema,
    status: questionnaireSyncEventStatusSchema,
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    attemptedAt: isoDateTimeSchema.nullable(),
    confirmedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const questionnaireAuditTimelineEntrySchema = z
    .object({
    id: idSchema,
    action: z.enum([
        'CREATE',
        'UPDATE',
        'DELETE',
        'LOGIN',
        'LOGOUT',
        'EXPORT',
        'POST',
        'APPROVE',
        'CANCEL',
        'VIEW',
    ]),
    entity: z.string().trim().min(1).max(100),
    entityId: idSchema,
    actorUserId: idSchema.nullable(),
    actorName: z.string().trim().min(1).max(500).nullable(),
    occurredAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireRunDetailSchema = z
    .object({
    run: questionnaireRunSchema,
    definition: questionnaireDefinitionSchema,
    /** Server-authoritative template + tenant policy decision for employee offline storage. */
    secureOfflineAllowed: z.boolean(),
    answers: z.array(questionnaireAnswerSchema),
    attachments: z.array(questionnaireAttachmentSchema),
    productAnswers: z.array(questionnaireProductAnswerSchema),
    consents: z.array(questionnaireConsentSchema),
    revisions: z.array(questionnaireRunRevisionSchema),
    syncEvents: z.array(questionnaireSyncEventSchema),
    completionActions: z.array(questionnaireCompletionActionSchema),
    auditTimeline: z.array(questionnaireAuditTimelineEntrySchema),
})
    .strict();
export const questionnaireOfflineSyncSchema = z
    .object({
    runId: idSchema,
    clientRunId: z.string().uuid(),
    baseLockVersion: z.number().int().min(1),
    events: z.array(questionnaireSyncEventInputSchema).min(1).max(500),
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
})
    .strict();
export const questionnaireOfflineSyncResponseSchema = z
    .object({
    runId: idSchema,
    lockVersion: z.number().int().min(1),
    syncStatus: questionnaireSyncStatusSchema,
    acceptedClientEventIds: z.array(z.string().uuid()),
    rejectedClientEventIds: z.array(z.string().uuid()),
    serverUpdatedAt: isoDateTimeSchema,
})
    .strict();
/**
 * A deliberately small, price-free product projection which may be persisted
 * encrypted for an offline-enabled published questionnaire version.
 */
export const questionnaireOfflineProductBundleSchema = z
    .object({
    questionKey: questionnaireKeySchema,
    products: z.array(questionnaireProductOptionSchema).max(QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS),
    totalAvailable: z.number().int().min(0),
    truncated: z.boolean(),
})
    .strict()
    .superRefine((bundle, context) => {
    if (bundle.products.some((product) => product.price !== null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['products'],
            message: 'Offline product bundles must not contain prices.',
        });
    }
    if (bundle.totalAvailable < bundle.products.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['totalAvailable'],
            message: 'totalAvailable must include every bundled product.',
        });
    }
    if (bundle.truncated !== bundle.totalAvailable > bundle.products.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['truncated'],
            message: 'truncated must reflect whether products were omitted.',
        });
    }
});
/** Immutable, authenticated server package persisted only inside the encrypted store. */
export const questionnaireOfflineTemplatePackageSchema = z
    .object({
    schemaVersion: z.literal(1),
    templateId: idSchema,
    templateVersionId: idSchema,
    versionNumber: z.number().int().min(1),
    title: z.string().trim().min(1).max(500),
    definitionChecksum: z.string().regex(/^[a-f0-9]{64}$/u),
    publishedAt: isoDateTimeSchema,
    definition: questionnaireDefinitionSchema,
    productBundles: z
        .array(questionnaireOfflineProductBundleSchema)
        .max(QUESTIONNAIRE_MAX_QUESTIONS),
})
    .strict()
    .superRefine((offlinePackage, context) => {
    if (!offlinePackage.definition.offline.enabled) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['definition', 'offline', 'enabled'],
            message: 'Only offline-enabled definitions may be packaged.',
        });
    }
    const questionKeys = new Set(offlinePackage.definition.sections.flatMap((section) => section.questions.map((question) => question.key)));
    const productQuestionKeys = new Set(offlinePackage.definition.sections.flatMap((section) => section.questions
        .filter((question) => [
        'PRODUCT_SINGLE',
        'PRODUCT_MULTI',
        'PRODUCT_CATALOG',
        'PRODUCT_QUANTITY_LIST',
    ].includes(question.type))
        .map((question) => question.key)));
    const bundledKeys = new Set();
    offlinePackage.productBundles.forEach((bundle, index) => {
        if (!questionKeys.has(bundle.questionKey) || !productQuestionKeys.has(bundle.questionKey)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['productBundles', index, 'questionKey'],
                message: 'An offline product bundle must reference a product question.',
            });
        }
        if (bundledKeys.has(bundle.questionKey)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['productBundles', index, 'questionKey'],
                message: 'An offline product bundle question key must be unique.',
            });
        }
        bundledKeys.add(bundle.questionKey);
    });
    const productCount = offlinePackage.productBundles.reduce((total, bundle) => total + bundle.products.length, 0);
    if (productCount > offlinePackage.definition.offline.preloadProductLimit) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['productBundles'],
            message: 'The package exceeds the template product preload limit.',
        });
    }
});
export const questionnaireOfflinePackageListResponseSchema = z
    .object({
    generatedAt: isoDateTimeSchema,
    packages: z
        .array(questionnaireOfflineTemplatePackageSchema)
        .max(QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS),
})
    .strict();
export const questionnaireLocalSyncStateSchema = z.enum([
    'LOCAL_ONLY',
    'PENDING',
    'SYNCING',
    'SYNCED',
    'FAILED',
    'CONFLICT',
]);
/**
 * Encrypted local start/run state. A stable clientRunId makes reconnect retries
 * resolve to exactly one authoritative server run.
 */
export const questionnaireOfflineRunEnvelopeSchema = z
    .object({
    schemaVersion: z.literal(1),
    audience: z.enum(['EMPLOYEE', 'CUSTOMER']),
    clientRunId: z.string().uuid(),
    serverRunId: idSchema.nullable(),
    templateVersionId: idSchema,
    definitionChecksum: z.string().regex(/^[a-f0-9]{64}$/u),
    startRequest: questionnaireRunCreateSchema,
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
    currentSectionKey: questionnaireKeySchema.nullable(),
    baseLockVersion: z.number().int().min(1).nullable(),
    serverStarted: z.boolean().default(false),
    completionRequested: z.boolean().default(false),
    syncState: questionnaireLocalSyncStateSchema,
    lastErrorCode: z.string().trim().min(1).max(200).nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((envelope, context) => {
    if (envelope.startRequest.clientRunId !== envelope.clientRunId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['startRequest', 'clientRunId'],
            message: 'The start request must use the envelope clientRunId.',
        });
    }
    if (envelope.startRequest.templateVersionId !== envelope.templateVersionId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['startRequest', 'templateVersionId'],
            message: 'The start request must use the packaged template version.',
        });
    }
    if (envelope.serverRunId === null && envelope.baseLockVersion !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['baseLockVersion'],
            message: 'A local-only run cannot have a server lock version.',
        });
    }
    if (envelope.serverRunId === null && envelope.serverStarted) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['serverStarted'],
            message: 'A local-only run cannot be marked as started on the server.',
        });
    }
});
/** Current page snapshot for a reload while the network is unavailable. */
export const questionnaireOfflinePageEnvelopeSchema = z
    .object({
    schemaVersion: z.literal(1),
    clientRunId: z.string().uuid(),
    serverRunId: idSchema.nullable(),
    templateVersionId: idSchema,
    definitionChecksum: z.string().regex(/^[a-f0-9]{64}$/u),
    sectionKey: questionnaireKeySchema,
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
    updatedAt: isoDateTimeSchema,
})
    .strict();
/**
 * Minimal customer handoff page persisted for a network-less reload. It remains
 * bound to the short server-issued customer-session expiry and contains no ERP shell data.
 */
export const questionnaireCustomerOfflinePageEnvelopeSchema = z
    .object({
    schemaVersion: z.literal(1),
    routeRunId: idSchema,
    page: questionnaireCustomerActiveRunSchema,
    storedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((envelope, context) => {
    if (envelope.page.runId !== envelope.routeRunId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['page', 'runId'],
            message: 'The cached customer page must match its route run.',
        });
    }
    if (!envelope.page.secureOfflineAllowed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['page', 'secureOfflineAllowed'],
            message: 'A customer page may only be cached when secure offline use is allowed.',
        });
    }
});
const questionnaireCustomerOfflineLocationSchema = z
    .object({
    sectionKey: questionnaireKeySchema,
    questionKey: questionnaireKeySchema.nullable(),
})
    .strict();
export const questionnaireCustomerOfflineTransitionSchema = z
    .object({
    clientEventId: z.string().uuid(),
    idempotencyKey: z.string().uuid(),
    source: questionnaireCustomerOfflineLocationSchema,
    target: questionnaireCustomerOfflineLocationSchema,
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
})
    .strict();
/**
 * Encrypted customer navigation journal. The server location is frozen at the
 * last acknowledged page, while adjacent offline transitions are replayed in
 * order with their original idempotency keys after reconnecting.
 */
export const questionnaireCustomerOfflineStateSchema = z
    .object({
    schemaVersion: z.literal(1),
    runId: idSchema,
    clientRunId: z.string().uuid(),
    baseLockVersion: z.number().int().min(1),
    serverLocation: questionnaireCustomerOfflineLocationSchema,
    currentLocation: questionnaireCustomerOfflineLocationSchema,
    currentAnswers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
    pendingTransitions: z.array(questionnaireCustomerOfflineTransitionSchema).max(500),
    syncEventId: z.string().uuid(),
    completionRequested: z.boolean(),
    completionIdempotencyKey: z.string().uuid(),
    updatedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((state, context) => {
    let expected = state.serverLocation;
    state.pendingTransitions.forEach((transition, index) => {
        if (transition.source.sectionKey !== expected.sectionKey ||
            transition.source.questionKey !== expected.questionKey) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['pendingTransitions', index, 'source'],
                message: 'Offline customer transitions must form one continuous navigation journal.',
            });
        }
        expected = transition.target;
    });
    if (expected.sectionKey !== state.currentLocation.sectionKey ||
        expected.questionKey !== state.currentLocation.questionKey) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['currentLocation'],
            message: 'The current customer page must match the end of the navigation journal.',
        });
    }
});
/** Minimal encrypted partner context used only to authorize an offline start request. */
export const questionnaireOfflinePartnerContextSchema = z
    .object({
    schemaVersion: z.literal(1),
    partnerId: idSchema,
    displayName: z.string().trim().min(1).max(500),
    isCustomer: z.boolean(),
    isProspect: z.boolean(),
    isSupplier: z.boolean(),
    cachedAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireCustomerOfflineDefinitionEnvelopeSchema = z
    .object({
    schemaVersion: z.literal(1),
    routeRunId: idSchema,
    templateVersionId: idSchema,
    definition: questionnaireCustomerDefinitionSchema,
    productBundles: z
        .array(questionnaireOfflineProductBundleSchema)
        .max(QUESTIONNAIRE_MAX_QUESTIONS),
    expiresAt: isoDateTimeSchema,
    storedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((envelope, context) => {
    const productQuestionKeys = new Set(envelope.definition.sections.flatMap((section) => section.questions
        .filter((question) => [
        'PRODUCT_SINGLE',
        'PRODUCT_MULTI',
        'PRODUCT_CATALOG',
        'PRODUCT_QUANTITY_LIST',
    ].includes(question.type))
        .map((question) => question.key)));
    envelope.productBundles.forEach((bundle, index) => {
        if (!productQuestionKeys.has(bundle.questionKey)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['productBundles', index, 'questionKey'],
                message: 'A customer offline product bundle must reference a visible product question.',
            });
        }
    });
});
export const questionnaireAnalyticsQuerySchema = z
    .object({
    templateId: idSchema.optional(),
    versionNumber: z.coerce.number().int().min(1).optional(),
    questionKey: questionnaireKeySchema.optional(),
    partnerRole: z.enum(['CUSTOMER', 'PROSPECT', 'SUPPLIER', 'PARTNERLESS']).optional(),
    productId: idSchema.optional(),
    dateFrom: isoDateTimeSchema.optional(),
    dateTo: isoDateTimeSchema.optional(),
})
    .strict()
    .refine((query) => query.dateFrom === undefined || query.dateTo === undefined || query.dateFrom <= query.dateTo, { path: ['dateTo'], message: 'dateTo must not be before dateFrom.' });
const questionnaireAnalyticsBucketSchema = z
    .object({
    value: z.union([z.string(), z.number().finite(), z.boolean()]),
    label: z.string().trim().min(1).max(500),
    count: z.number().int().min(0),
    percentage: z.number().min(0).max(100),
})
    .strict();
const questionnaireAnalyticsMetricBaseSchema = z
    .object({
    questionKey: questionnaireKeySchema,
    analyticsKey: questionnaireKeySchema,
    label: questionnaireLabelSchema,
    questionType: questionnaireQuestionTypeSchema,
    answerCount: z.number().int().min(0),
})
    .strict();
export const questionnaireAnalyticsQuestionMetricSchema = z.discriminatedUnion('kind', [
    questionnaireAnalyticsMetricBaseSchema.extend({
        kind: z.literal('DISTRIBUTION'),
        buckets: z
            .array(questionnaireAnalyticsBucketSchema)
            .max(QUESTIONNAIRE_MAX_OPTIONS_PER_QUESTION),
    }),
    questionnaireAnalyticsMetricBaseSchema.extend({
        kind: z.literal('NUMERIC'),
        average: z.number().finite().nullable(),
        minimum: z.number().finite().nullable(),
        maximum: z.number().finite().nullable(),
        buckets: z.array(questionnaireAnalyticsBucketSchema).max(101),
    }),
]);
export const questionnaireAnalyticsResponseSchema = z
    .object({
    summary: z
        .object({
        startedCount: z.number().int().min(0),
        completedCount: z.number().int().min(0),
        abandonedCount: z.number().int().min(0),
        completionRate: z.number().min(0).max(100),
        abandonmentRate: z.number().min(0).max(100),
        averageDurationSeconds: z.number().min(0).nullable(),
    })
        .strict(),
    questions: z.array(questionnaireAnalyticsQuestionMetricSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
    productInterests: z.array(z
        .object({
        productId: idSchema,
        productNameSnapshot: z.string().trim().min(1).max(500),
        articleNumberSnapshot: z.string().trim().min(1).max(100),
        selectionCount: z.number().int().min(0),
        interest: z
            .object({
            none: z.number().int().min(0),
            low: z.number().int().min(0),
            medium: z.number().int().min(0),
            high: z.number().int().min(0),
        })
            .strict(),
        desiredQuantityTotal: nonNegativeQuantitySchema.nullable(),
        baseUnitSnapshot: z.string().trim().min(1).max(50),
    })
        .strict()),
    partnerRoles: z.array(z
        .object({
        role: z.enum(['CUSTOMER', 'PROSPECT', 'SUPPLIER', 'PARTNERLESS']),
        count: z.number().int().min(0),
    })
        .strict()),
    trend: z.array(z
        .object({
        date: questionnaireIsoDateSchema,
        startedCount: z.number().int().min(0),
        completedCount: z.number().int().min(0),
        abandonedCount: z.number().int().min(0),
    })
        .strict()),
})
    .strict();
export const questionnaireSettingsSchema = z
    .object({
    retentionDays: z.number().int().min(1).max(3_650).default(QUESTIONNAIRE_RETENTION_DAYS_DEFAULT),
    abandonedDraftCleanupDays: z
        .number()
        .int()
        .min(1)
        .max(365)
        .default(QUESTIONNAIRE_DRAFT_CLEANUP_DAYS_DEFAULT),
    customerSessionTtlMinutes: z
        .number()
        .int()
        .min(5)
        .max(120)
        .default(QUESTIONNAIRE_CUSTOMER_SESSION_TTL_MINUTES_DEFAULT),
    customerIdleLockMinutes: z
        .number()
        .int()
        .min(1)
        .max(60)
        .default(QUESTIONNAIRE_CUSTOMER_IDLE_LOCK_MINUTES_DEFAULT),
    offlineCatalogMaxProducts: z
        .number()
        .int()
        .min(0)
        .max(QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS)
        .default(QUESTIONNAIRE_MAX_OFFLINE_PRODUCTS),
    allowSensitiveOffline: z.boolean().default(false),
    createPdfOnCompletion: z.boolean().default(true),
    attachPdfToPartner: z.boolean().default(false),
})
    .strict();
export const questionnaireRunRevisionCreateSchema = z
    .object({
    baseRevisionNumber: z.number().int().min(0),
    reason: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    answers: z.array(questionnaireSubmittedAnswerSchema).max(QUESTIONNAIRE_MAX_QUESTIONS),
})
    .strict();
export const questionnaireConsentRevokeSchema = z
    .object({
    reason: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
})
    .strict();
export const questionnaireRunAnonymizeSchema = z
    .object({
    reason: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    confirmed: z.literal(true),
})
    .strict();
const questionnaireRunLegalHoldMutationBaseSchema = z.object({
    lockVersion: z.number().int().min(1),
    idempotencyKey: z.string().uuid(),
});
export const questionnaireRunLegalHoldMutationSchema = z.discriminatedUnion('action', [
    questionnaireRunLegalHoldMutationBaseSchema
        .extend({
        action: z.literal('SET'),
        reason: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
        until: isoDateTimeSchema.nullable().default(null),
    })
        .strict(),
    questionnaireRunLegalHoldMutationBaseSchema
        .extend({
        action: z.literal('RELEASE'),
        reason: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH),
    })
        .strict(),
]);
export const questionnaireRunLegalHoldSchema = z
    .object({
    runId: idSchema,
    status: z.enum(['NONE', 'ACTIVE', 'EXPIRED']),
    reason: z.string().trim().min(1).max(QUESTIONNAIRE_MAX_TEXT_LENGTH).nullable(),
    until: isoDateTimeSchema.nullable(),
    lockVersion: z.number().int().min(1),
    updatedAt: isoDateTimeSchema,
})
    .strict()
    .superRefine((hold, context) => {
    if (hold.status === 'NONE' && (hold.reason !== null || hold.until !== null)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['status'],
            message: 'A run without a legal hold cannot contain legal-hold details.',
        });
    }
    if (hold.status !== 'NONE' && hold.reason === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: 'An active or expired legal hold requires a reason.',
        });
    }
    if (hold.status === 'EXPIRED' && hold.until === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['until'],
            message: 'An expired legal hold requires an end date.',
        });
    }
});
export const questionnaireExportFormatSchema = z.enum(['PDF', 'CSV', 'JSON', 'MEDIA_ZIP']);
export const questionnaireExportRequestSchema = z
    .object({
    format: questionnaireExportFormatSchema,
    runIds: z.array(idSchema).min(1).max(1_000),
    includeAttachments: z.boolean().default(false),
})
    .strict();
export const questionnaireResultsExportRequestSchema = z
    .object({
    format: z.enum(['CSV', 'JSON']),
    filters: questionnaireRunListQuerySchema,
})
    .strict();
export const questionnaireExportJobSchema = z
    .object({
    id: idSchema,
    status: z.enum(['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED']),
    format: z.enum(['CSV', 'JSON']),
    fileName: z.string().nullable(),
    downloadAvailable: z.boolean(),
    errorCode: z.string().nullable(),
    progressPercent: z.number().int().min(0).max(100),
    rowCount: z.number().int().min(0).nullable(),
    createdAt: isoDateTimeSchema,
    completedAt: isoDateTimeSchema.nullable(),
    expiresAt: isoDateTimeSchema,
})
    .strict();
export const questionnaireExportJobListResponseSchema = z
    .object({ data: z.array(questionnaireExportJobSchema) })
    .strict();
//# sourceMappingURL=questionnaires.js.map